import {
  AUTO_SCROLL_MODE,
  getCampaignAutoScrollMode,
  getCampaignReadStatusBorderWidth,
  getCampaignTileBlockMaxWidth,
  getCampaignTileGapVertical,
  getRenderedCampaignGroupGap,
  isCampaignLoopPlaybackEnabled,
} from '@/helpers/campaignSettings';
import { showToast } from '@/helpers/toast';
import { enableCampaignAutoScroll } from '@/modules/actions/enableCampaignAutoScroll';

const INSERT_SELECTOR_QUERY_KEY = 'storeeel_insert_point_selector_token';
const INSERT_SELECTOR_DEVICE_QUERY_KEY = 'storeeel_insert_point_selector_device';
const INSERT_SELECTOR_FRAME_QUERY_KEY = 'storeeel_insert_point_selector_frame';
const INSERT_SELECTOR_PLACEHOLDER_ID = 'storeeel-insert-point-placeholder';
const INSERT_SELECTOR_PLACEHOLDER_CLASS = 'storeeel-insert-point-placeholder';
// タイル型は本番描画(div > .storeeell_base_X)と同じく、外側(placeholder)は中央寄せの役割のみを持ち、
// margin/padding・行間gap・グリッド全体の最大幅はこの内側ラッパーに適用する。
const INSERT_SELECTOR_TILE_BASE_CLASS = 'storeeel-insert-point-tile-base';
const INSERT_SELECTOR_DRAG_HANDLE_CLASS = 'storeeel-insert-point-selector-drag-handle';
const INSERT_SELECTOR_OVERLAY_CLASS = 'storeeel-insert-point-selector-overlay';
const INSERT_SELECTOR_UI_ROOT_ATTRIBUTE = 'data-storeeel-insert-selector-ui-root';
const INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE = 'data-storeeel-insert-selector-highlight-host';
const INSERT_SELECTOR_ATTRIBUTE = 'data-storeeel-insert-target';
const INSERT_SELECTOR_EVENT_NAME = 'storeeel:insert-point-selected';
const INSERT_SELECTOR_SETTINGS_MESSAGE_TYPE = 'storeeel:simple-setting-settings-changed';
const INSERT_SELECTOR_SELECTION_MESSAGE_TYPE = 'storeeel:simple-setting-selection-changed';
const INSERT_SELECTOR_STATUS_MESSAGE_TYPE = 'storeeel:simple-setting-status-changed';
const INSERT_SELECTOR_DEFAULT_FRAME = 'story';
const INSERT_SELECTOR_DEFAULT_POSITION = 'outer_before';
const INSERT_SELECTOR_ALLOWED_DEVICES = new Set(['pc', 'mobile']);
const INSERT_SELECTOR_ALLOWED_FRAMES = new Set(['story', 'reel', 'tile']);
const INSERT_SELECTOR_POSITIONS = [
  { value: 'outer_before', label: '要素の真上' },
  { value: 'outer_after', label: '要素の真下' },
  { value: 'inner_start', label: '要素内の先頭' },
  { value: 'inner_end', label: '要素内の末尾' },
];
const INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN = 8;
const INSERT_SELECTOR_DRAG_POSITION_HYSTERESIS_PX = 12;
const INSERT_SELECTOR_DRAG_UPDATE_INTERVAL_MS = 50;
const INSERT_SELECTOR_DRAG_AUTO_SCROLL_EDGE_PX = 96;
const INSERT_SELECTOR_DRAG_AUTO_SCROLL_MAX_STEP_PX = 18;
const INSERT_SELECTOR_DRAG_AUTO_SCROLL_INTERVAL_MS = 16;
const INSERT_SELECTOR_MENU_TOGGLE_VISIBLE_WIDTH = 32;
const INSERT_SELECTOR_VISIBLE_RECT_SAMPLE_STEP_PX = 8;
const EXCLUDED_TAG_NAMES = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'HEAD', 'HTML', 'BODY']);
const GENERIC_CLASS_NAMES = new Set([
  'active',
  'block',
  'card',
  'col',
  'column',
  'container',
  'current',
  'disabled',
  'flex',
  'grid',
  'hidden',
  'inner',
  'item',
  'js',
  'large',
  'medium',
  'open',
  'outer',
  'pc',
  'row',
  'section',
  'selected',
  'show',
  'small',
  'sp',
  'wrap',
  'wrapper',
]);
const UNSAFE_INSERT_TARGET_TAG_NAMES = new Set([
  'A',
  'BUTTON',
  'IMG',
  'PICTURE',
  'SOURCE',
  'SVG',
  'VIDEO',
  'CANVAS',
  'IFRAME',
  'INPUT',
  'TEXTAREA',
  'SELECT',
  'LABEL',
  'SPAN',
  'STRONG',
  'EM',
  'SMALL',
  'BR',
]);
const DECORATIVE_CLASS_NAME_PATTERNS = [
  /(^|[-_])txt($|[-_\d])/i,
  /(^|[-_])text($|[-_\d])/i,
  /(^|[-_])ico($|[-_\d])/i,
  /(^|[-_])icon($|[-_\d])/i,
  /(^|[-_])img($|[-_\d])/i,
  /(^|[-_])image($|[-_\d])/i,
  /(^|[-_])photo($|[-_\d])/i,
  /(^|[-_])thumb($|[-_\d])/i,
  /(^|[-_])caption($|[-_\d])/i,
  /(^|[-_])label($|[-_\d])/i,
  /(^|[-_])btn($|[-_\d])/i,
  /(^|[-_])button($|[-_\d])/i,
  /(^|[-_])link($|[-_\d])/i,
  /(^|[-_])media($|[-_\d])/i,
];

const escapeCssValue = (value) => {
  if (window.CSS?.escape) return window.CSS.escape(value);
  return String(value).replace(/["\\]/g, '\\$&');
};

const getXPathSnapshot = (expression, contextNode = document) => {
  return document.evaluate(
    expression,
    contextNode,
    null,
    XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
    null
  );
};

const getXPathMatchCount = (expression, contextNode = document) => {
  return getXPathSnapshot(expression, contextNode).snapshotLength;
};

const isStableClassName = (className) => {
  if (!className || GENERIC_CLASS_NAMES.has(className)) return false;
  if (className.length < 3) return false;
  if (/^\d/.test(className)) return false;
  if (/^[A-Fa-f0-9_-]{8,}$/.test(className)) return false;
  if (/^storeeel-/.test(className)) return false;
  return !/^(css|jsx|sc|swiper)-/.test(className);
};

const getStableClassNames = (element) => {
  if (!element?.classList) return [];
  return Array.from(element.classList).filter(isStableClassName);
};

const isDecorativeClassName = (className) => {
  return DECORATIVE_CLASS_NAME_PATTERNS.some((pattern) => pattern.test(className));
};

const getSafeStableClassNames = (element) => {
  return getStableClassNames(element).filter((className) => !isDecorativeClassName(className));
};

const isStoreeelElement = (element) => {
  return Boolean(
    element?.closest?.(
      [
        '[data-storeeel-campaign-id]',
        '.storeeel-campaign-container',
        `#${INSERT_SELECTOR_PLACEHOLDER_ID}`,
        '[data-storeeel-insert-selector-placeholder]',
        '[data-storeeel-insert-selector-menu]',
        '[data-storeeel-insert-selector-confirm]',
        '[data-storeeel-insert-selector-drag-handle]',
        '[data-storeeel-insert-selector-overlay]',
        '[data-storeeel-insert-selector-device-warning]',
        `[${INSERT_SELECTOR_UI_ROOT_ATTRIBUTE}]`,
        `[${INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE}]`,
        `.${INSERT_SELECTOR_OVERLAY_CLASS}`,
        '.Toastify',
        '[data-storeeel-insert-selector-toast="true"]',
      ].join(', ')
    )
  );
};

const isExcludedElement = (element) => {
  if (!(element instanceof Element)) return true;
  if (EXCLUDED_TAG_NAMES.has(element.tagName)) return true;
  return isStoreeelElement(element);
};

const isPointOverInsertSelectorMenu = (clientX, clientY) => {
  const menu = document.querySelector('[data-storeeel-insert-selector-menu="true"]')
    || Array.from(document.querySelectorAll(`[${INSERT_SELECTOR_UI_ROOT_ATTRIBUTE}="true"]`))
      .map((root) => root.shadowRoot?.querySelector('[data-storeeel-insert-selector-menu="true"]'))
      .find(Boolean);
  if (!menu) return false;
  const rect = menu.getBoundingClientRect();
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
};

const getInsertSelectorShadowHighlight = () => (
  Array.from(document.querySelectorAll(`[${INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE}="true"]`))
    .find(Boolean)
    || Array.from(document.querySelectorAll(`[${INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE}="true"]`))
      .map((root) => root.shadowRoot?.querySelector('.storeeel-insert-point-selector-shadow-highlight'))
      .find(Boolean)
    || Array.from(document.querySelectorAll(`[${INSERT_SELECTOR_UI_ROOT_ATTRIBUTE}="true"]`))
      .map((root) => root.shadowRoot?.querySelector('[data-storeeel-insert-selector-highlight="true"]'))
      .find(Boolean)
    || document.querySelector('[data-storeeel-insert-selector-highlight="true"]')
);

const getInsertSelectorHighlightHost = (placeholder) => {
  return placeholder?.querySelector?.(`:scope > [${INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE}="true"]`) || null;
};

const getInsertSelectorHighlight = (placeholder) => {
  return getInsertSelectorHighlightHost(placeholder);
};

const isPointOverVisibleInsertSelectorHighlight = (placeholder, clientX, clientY) => {
  if (!placeholder) return false;
  const highlight = getInsertSelectorHighlight(placeholder) || getInsertSelectorShadowHighlight();
  if (!highlight || window.getComputedStyle(highlight).display === 'none') return false;
  const rect = highlight.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
};

const hasVisibleInsertSelectorHighlight = (placeholder) => {
  if (!placeholder) return false;
  const highlight = getInsertSelectorHighlight(placeholder) || getInsertSelectorShadowHighlight();
  if (!highlight || window.getComputedStyle(highlight).display === 'none') return false;
  const rect = highlight.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const isPointOverElement = (element, clientX, clientY) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
};

const getInsertSelectorOccupiedRect = (placeholder) => {
  // 簡単設定ではプレビュー内のキャンペーン単体ではなく、差し込み枠全体を操作対象として示す。
  const rect = placeholder.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
  };
};

const intersectRects = (...rects) => {
  const visibleRect = rects.reduce((current, rect) => ({
    left: Math.max(current.left, rect.left),
    top: Math.max(current.top, rect.top),
    right: Math.min(current.right, rect.right),
    bottom: Math.min(current.bottom, rect.bottom),
  }));

  if (visibleRect.right <= visibleRect.left || visibleRect.bottom <= visibleRect.top) return null;
  return visibleRect;
};

const isPointInsideRect = (rect, clientX, clientY) => (
  clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
);

const hasFrontElementBeforeTarget = (elements, target) => {
  for (const element of elements) {
    if (element === target || target.contains(element)) return false;
    if (isStoreeelElement(element)) continue;
    return true;
  }
  return false;
};

const isMarginPointCoveredByFloatingElement = (elements) => {
  const topElement = elements.find((element) => !isStoreeelElement(element));
  if (!topElement) return false;
  const style = window.getComputedStyle(topElement);
  const zIndex = Number.parseInt(style.zIndex, 10);

  // margin 領域は DOM hit test に出ないため、固定/粘着/前面配置の要素だけを被りとして扱う。
  return ['fixed', 'sticky'].includes(style.position)
    || (style.position !== 'static' && Number.isFinite(zIndex) && zIndex >= 0);
};

const isVisibleOccupiedPoint = (target, borderBoxRect, clientX, clientY) => {
  if (clientX < 0 || clientY < 0) return false;
  const elements = document.elementsFromPoint(clientX, clientY);
  if (isPointInsideRect(borderBoxRect, clientX, clientY)) {
    return !hasFrontElementBeforeTarget(elements, target);
  }

  return !isMarginPointCoveredByFloatingElement(elements);
};

const isOccupiedRectFullyVisible = (target, borderBoxRect, rect) => {
  const inset = 0.5;
  const points = [
    [rect.left + inset, rect.top + inset],
    [rect.right - inset, rect.top + inset],
    [rect.right - inset, rect.bottom - inset],
    [rect.left + inset, rect.bottom - inset],
    [rect.left + ((rect.right - rect.left) / 2), rect.top + ((rect.bottom - rect.top) / 2)],
  ];

  return points.every(([clientX, clientY]) => isVisibleOccupiedPoint(target, borderBoxRect, clientX, clientY));
};

const getVisibleOccupiedRect = (target, occupiedRect) => {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const candidateRect = intersectRects(
    occupiedRect,
    { left: 0, top: 0, right: viewportWidth, bottom: viewportHeight }
  );
  if (!candidateRect) return null;

  if (target?.dataset?.storeeelInsertSelectorPlaceholder === 'true') {
    // hover 中は placeholder 本体が hit test から外れるため、空白部分も含めて枠全体を可視範囲として扱う。
    return candidateRect;
  }

  const borderBoxRect = target.getBoundingClientRect();
  if (isOccupiedRectFullyVisible(target, borderBoxRect, candidateRect)) return candidateRect;

  const step = INSERT_SELECTOR_VISIBLE_RECT_SAMPLE_STEP_PX;
  let visibleLeft = Infinity;
  let visibleTop = Infinity;
  let visibleRight = -Infinity;
  let visibleBottom = -Infinity;

  for (let y = candidateRect.top; y < candidateRect.bottom; y += step) {
    const sampleY = Math.min(y + (step / 2), candidateRect.bottom - 0.5);
    for (let x = candidateRect.left; x < candidateRect.right; x += step) {
      const sampleX = Math.min(x + (step / 2), candidateRect.right - 0.5);
      if (!isVisibleOccupiedPoint(target, borderBoxRect, sampleX, sampleY)) continue;
      visibleLeft = Math.min(visibleLeft, x);
      visibleTop = Math.min(visibleTop, y);
      visibleRight = Math.max(visibleRight, Math.min(x + step, candidateRect.right));
      visibleBottom = Math.max(visibleBottom, Math.min(y + step, candidateRect.bottom));
    }
  }

  if (!Number.isFinite(visibleLeft) || visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
    return null;
  }

  return {
    left: visibleLeft,
    top: visibleTop,
    right: visibleRight,
    bottom: visibleBottom,
  };
};

const isHorizontalWheelEvent = (event) => {
  const absDeltaX = Math.abs(Number(event?.deltaX) || 0);
  const absDeltaY = Math.abs(Number(event?.deltaY) || 0);
  return absDeltaX > 0 && absDeltaX >= absDeltaY;
};

const isSelectableLayoutElement = (element) => {
  if (!(element instanceof Element)) return false;
  const style = window.getComputedStyle(element);
  return ['block', 'flex', 'grid', 'flow-root', 'table', 'list-item'].includes(style.display);
};

const isSafeInsertTargetElement = (element) => {
  if (!(element instanceof Element)) return false;
  if (UNSAFE_INSERT_TARGET_TAG_NAMES.has(element.tagName)) return false;
  return isSelectableLayoutElement(element);
};

const getElementDepth = (element) => {
  let depth = 0;
  let current = element;
  while (current?.parentElement) {
    depth += 1;
    current = current.parentElement;
  }
  return depth;
};

const getVerticalDistanceToRect = (rect, clientY) => {
  if (clientY >= rect.top && clientY <= rect.bottom) return 0;
  return Math.min(Math.abs(clientY - rect.top), Math.abs(clientY - rect.bottom));
};

const getHorizontalDistanceToRect = (rect, clientX) => {
  if (clientX >= rect.left && clientX <= rect.right) return 0;
  return Math.min(Math.abs(clientX - rect.left), Math.abs(clientX - rect.right));
};

const getDistanceToRect = (rect, clientX, clientY) => {
  const horizontalDistance = getHorizontalDistanceToRect(rect, clientX);
  const verticalDistance = getVerticalDistanceToRect(rect, clientY);
  return Math.hypot(horizontalDistance, verticalDistance);
};

const getPointDragTarget = (origin) => {
  let current = origin instanceof Element ? origin : null;
  while (current && current !== document.documentElement) {
    if (!isExcludedElement(current) && isSafeInsertTargetElement(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

const collectNearbyTargetElements = (baseTarget) => {
  if (!(baseTarget instanceof Element)) return [];
  const scopes = [
    baseTarget,
    baseTarget.parentElement,
    baseTarget.parentElement?.parentElement,
  ].filter(Boolean);
  const elements = [];
  const seen = new Set();

  scopes.forEach((scope) => {
    [scope, ...Array.from(scope.querySelectorAll('*')).slice(0, 300)].forEach((element) => {
      if (seen.has(element)) return;
      seen.add(element);
      elements.push(element);
    });
  });

  return elements;
};

const getNearestDragTarget = (baseTarget, clientX, clientY) => {
  if (!(baseTarget instanceof Element)) return null;

  const candidates = collectNearbyTargetElements(baseTarget).filter((element) => (
    !isExcludedElement(element)
    && isSafeInsertTargetElement(element)
    && element.parentElement
  )).map((element) => {
    const rect = element.getBoundingClientRect();
    const verticalDistance = getVerticalDistanceToRect(rect, clientY);
    const horizontalDistance = getHorizontalDistanceToRect(rect, clientX);
    return {
      element,
      rect,
      containsPoint: clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom,
      verticalDistance,
      horizontalDistance,
      distance: getDistanceToRect(rect, clientX, clientY),
    };
  }).filter(({ rect, verticalDistance, horizontalDistance, distance }) => (
    rect.width > 0
    && rect.height > 0
    && (
      verticalDistance <= Math.max(64, rect.height / 2)
      || horizontalDistance <= Math.max(64, rect.width / 2)
      || distance <= 96
    )
  ));

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (a.element === baseTarget && a.rect.height >= 180 && b.element !== baseTarget && b.distance <= 96) return 1;
    if (b.element === baseTarget && b.rect.height >= 180 && a.element !== baseTarget && a.distance <= 96) return -1;
    if (a.containsPoint !== b.containsPoint) return a.containsPoint ? -1 : 1;
    if (a.distance !== b.distance) return a.distance - b.distance;
    if (a.verticalDistance !== b.verticalDistance) return a.verticalDistance - b.verticalDistance;
    const depthDiff = getElementDepth(b.element) - getElementDepth(a.element);
    if (depthDiff !== 0) return depthDiff;
    return (a.rect.width * a.rect.height) - (b.rect.width * b.rect.height);
  });

  return candidates[0].element;
};

const stabilizeDragInsertPosition = (target, clientY, nextPosition, fallbackPosition) => {
  if (!(target instanceof Element)) return nextPosition;
  if (!['outer_before', 'outer_after'].includes(nextPosition)) return nextPosition;
  if (!['outer_before', 'outer_after'].includes(fallbackPosition)) return nextPosition;
  if (nextPosition === fallbackPosition) return nextPosition;

  const rect = target.getBoundingClientRect();
  if (rect.height <= 0) return nextPosition;
  if (
    fallbackPosition === 'outer_before'
    && rect.height < 64
    && clientY >= rect.top
    && clientY <= rect.bottom
  ) {
    return fallbackPosition;
  }
  const lowerQuarter = rect.top + (rect.height * 0.75);

  if (fallbackPosition === 'outer_after') return fallbackPosition;
  if (fallbackPosition === 'outer_before' && clientY < lowerQuarter) return fallbackPosition;
  return nextPosition;
};

const getHoverTarget = (origin) => {
  let current = origin instanceof Element ? origin : null;
  let firstIdElement = null;
  let firstClassElement = null;
  let firstLayoutElement = null;

  while (current && current !== document.documentElement) {
    if (!isExcludedElement(current)) {
      const safeStableClassNames = getSafeStableClassNames(current);
      if (!firstIdElement && current.id && isSafeInsertTargetElement(current)) {
        firstIdElement = current;
      }
      if (!firstClassElement && safeStableClassNames.length > 0 && isSafeInsertTargetElement(current)) {
        firstClassElement = current;
      }
      if (!firstLayoutElement && isSafeInsertTargetElement(current)) {
        firstLayoutElement = current;
      }
    }
    current = current.parentElement;
  }

  // 親の id より、hover 起点に近い安定 class を優先して詳細な差し込み位置を選べるようにする。
  return firstClassElement || firstIdElement || firstLayoutElement || null;
};

const preferNearbyStableClassTarget = (currentTarget, originTarget) => {
  if (!(currentTarget instanceof Element)) return originTarget;
  if (!(originTarget instanceof Element)) return currentTarget;
  if (currentTarget === originTarget) return currentTarget;
  if (!currentTarget.contains(originTarget)) return currentTarget;
  if (getSafeStableClassNames(originTarget).length === 0) return currentTarget;

  // 座標判定が親要素へ滑った場合も、操作起点に近い安定 class を登録対象にする。
  if (currentTarget.id || getSafeStableClassNames(currentTarget).length === 0) {
    return originTarget;
  }

  return currentTarget;
};

export const resolveDragInsertPosition = (
  target,
  clientY,
  fallbackPosition = INSERT_SELECTOR_DEFAULT_POSITION
) => {
  if (!(target instanceof Element)) return fallbackPosition;
  if (!['outer_before', 'outer_after'].includes(fallbackPosition)) return fallbackPosition;

  const rect = target.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || rect.bottom;
  const visibleTop = Math.max(0, rect.top);
  const visibleBottom = Math.min(viewportHeight, rect.bottom ?? rect.top + rect.height);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  // 縦長要素では要素全体ではなく画面に見えている範囲を基準にし、下半分が画面外でも操作できるようにする。
  const middleY = visibleHeight > 0
    ? visibleTop + (visibleHeight / 2)
    : rect.top + (rect.height / 2);
  if (Math.abs(clientY - middleY) <= INSERT_SELECTOR_DRAG_POSITION_HYSTERESIS_PX) {
    return fallbackPosition;
  }

  return clientY < middleY ? 'outer_before' : 'outer_after';
};

const getInsertPointSelectorSearchParams = () => {
  return new URL(window.location.href).searchParams;
};

export const normalizeInsertPointSelectorDevice = (value) => {
  const normalizedValue = typeof value === 'string' ? value.toLowerCase() : '';
  return INSERT_SELECTOR_ALLOWED_DEVICES.has(normalizedValue) ? normalizedValue : null;
};

export const normalizeInsertPointSelectorFrame = (value) => {
  const normalizedValue = typeof value === 'string' ? value.toLowerCase() : '';
  return INSERT_SELECTOR_ALLOWED_FRAMES.has(normalizedValue) ? normalizedValue : INSERT_SELECTOR_DEFAULT_FRAME;
};

const ensurePlaceholderStyle = () => {
  if (document.getElementById('storeeel-insert-point-selector-style')) return;
  const style = document.createElement('style');
  style.id = 'storeeel-insert-point-selector-style';
  style.textContent = `
    .${INSERT_SELECTOR_OVERLAY_CLASS} {
      position: fixed;
      inset: 0;
      z-index: 2147483645;
      display: none;
      pointer-events: none;
      background: rgba(24, 24, 27, 0.34);
    }
    body[data-storeeel-insert-selector-edit-mode="position"] .${INSERT_SELECTOR_OVERLAY_CLASS} {
      display: block;
    }
    :host([data-storeeel-insert-selector-edit-mode="position"]) .${INSERT_SELECTOR_OVERLAY_CLASS} {
      display: block;
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-overlay-part] {
      position: fixed;
      display: none;
      pointer-events: none;
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-highlight="true"] {
      display: none;
    }
    .storeeel-insert-point-selector-inline-highlight {
      position: absolute;
      display: none;
      background: #ffffff;
      border-radius: 10px;
      pointer-events: none;
      z-index: 0;
    }
    .storeeel-insert-point-selector-shadow-highlight {
      position: absolute;
      inset: 0;
      display: block;
      background-color: #ffffff;
      background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) calc(100% - 18px), rgba(15, 23, 42, 0.14));
      border-radius: 10px;
      /* 簡単設定の切り抜き部分だけに影を付け、通常キャンペーン表示には影響させない。 */
      pointer-events: none;
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-overlay-corner] {
      position: fixed;
      display: none;
      width: 10px;
      height: 10px;
      pointer-events: none;
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-overlay-corner="top-left"] {
      background: radial-gradient(circle at bottom right, transparent 0 10px, rgba(24, 24, 27, 0.34) 10.5px);
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-overlay-corner="top-right"] {
      background: radial-gradient(circle at bottom left, transparent 0 10px, rgba(24, 24, 27, 0.34) 10.5px);
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-overlay-corner="bottom-right"] {
      background: radial-gradient(circle at top left, transparent 0 10px, rgba(24, 24, 27, 0.34) 10.5px);
    }
    .${INSERT_SELECTOR_OVERLAY_CLASS} [data-storeeel-insert-selector-overlay-corner="bottom-left"] {
      background: radial-gradient(circle at top right, transparent 0 10px, rgba(24, 24, 27, 0.34) 10.5px);
    }
    #${INSERT_SELECTOR_PLACEHOLDER_ID},
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS} {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 8px 0;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      overflow-x: auto;
      white-space: nowrap;
      scrollbar-width: none;
      pointer-events: auto;
      user-select: none;
      position: relative;
      isolation: isolate;
      z-index: auto;
    }
    .${INSERT_SELECTOR_TILE_BASE_CLASS} {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
    }
    body[data-storeeel-insert-selector-active="true"] .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-overlay-target="true"] .storeeel_campaign {
      position: relative;
      z-index: 1;
    }
    body[data-storeeel-insert-selector-active="true"] #${INSERT_SELECTOR_PLACEHOLDER_ID}[data-storeeel-insert-selector-overlay-target="true"],
    body[data-storeeel-insert-selector-active="true"] .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-overlay-target="true"] {
      overflow-x: hidden;
      z-index: 2147483646;
    }
    body[data-storeeel-insert-selector-active="true"] #${INSERT_SELECTOR_PLACEHOLDER_ID}[data-storeeel-insert-selector-overlay-target="true"]:not([data-storeeel-insert-selector-selected="true"]) {
      pointer-events: none;
    }
    body[data-storeeel-insert-selector-active="true"] #${INSERT_SELECTOR_PLACEHOLDER_ID}[data-storeeel-insert-selector-overlay-target="true"]:not([data-storeeel-insert-selector-selected="true"]) .storeeel_campaign {
      /* hoverプレビュー本体は hit test できるようにして、プレビュー上クリックでも現在の選択を確定する。 */
      pointer-events: auto;
    }
    #${INSERT_SELECTOR_PLACEHOLDER_ID}::-webkit-scrollbar,
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS}::-webkit-scrollbar {
      display: none;
    }
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"] {
      pointer-events: auto;
      cursor: grab;
      touch-action: none;
    }
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"]::before {
      content: none;
    }
    .${INSERT_SELECTOR_DRAG_HANDLE_CLASS} {
      --storeeel-insert-handle-dot: rgba(255, 255, 255, 1);
      --storeeel-insert-handle-background: rgba(80, 80, 90, 0.92);
      content: '';
      position: fixed;
      left: 0;
      top: 0;
      width: 50px;
      height: 18px;
      border-radius: 12px;
      cursor: grab;
      background-color: transparent;
      background-image:
        radial-gradient(circle, var(--storeeel-insert-handle-dot) 0 1.8px, transparent 1.9px),
        radial-gradient(circle, var(--storeeel-insert-handle-dot) 0 1.8px, transparent 1.9px),
        radial-gradient(circle, var(--storeeel-insert-handle-dot) 0 1.8px, transparent 1.9px),
        radial-gradient(circle, var(--storeeel-insert-handle-dot) 0 1.8px, transparent 1.9px),
        radial-gradient(circle, var(--storeeel-insert-handle-dot) 0 1.8px, transparent 1.9px),
        radial-gradient(circle, var(--storeeel-insert-handle-dot) 0 1.8px, transparent 1.9px);
      background-color: var(--storeeel-insert-handle-background);
      box-shadow: 0 1px 4px #d1d1d1;
      background-size: 7px 7px;
      background-position:
        14.5px 2px,
        21.5px 2px,
        28.5px 2px,
        14.5px 9px,
        21.5px 9px,
        28.5px 9px;
      background-repeat: no-repeat;
      pointer-events: auto;
      touch-action: none;
      user-select: none;
      z-index: 2147483646;
    }
    .${INSERT_SELECTOR_DRAG_HANDLE_CLASS}[data-storeeel-insert-selector-dragging="true"] {
      cursor: grabbing;
    }
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"][data-storeeel-insert-selector-dragging="true"] {
      cursor: grabbing;
    }
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"] {
      background: transparent;
    }
    body[data-storeeel-insert-selector-active="true"] .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"] .storeeel_campaign,
    body[data-storeeel-insert-selector-active="true"] .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"] .storeeel_campaign * {
      cursor: grab !important;
    }
    #${INSERT_SELECTOR_PLACEHOLDER_ID} .storeeel-insert-point-selector-item,
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS} .storeeel-insert-point-selector-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
    }
    #${INSERT_SELECTOR_PLACEHOLDER_ID} .storeeel-insert-point-selector-circle,
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS} .storeeel-insert-point-selector-circle {
      width: 58px;
      height: 58px;
      border-radius: 9999px;
      background: #d4d4d8;
      border: 2px solid #a1a1aa;
      box-sizing: border-box;
    }
    #${INSERT_SELECTOR_PLACEHOLDER_ID} .storeeel-insert-point-selector-label,
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS} .storeeel-insert-point-selector-label {
      width: 44px;
      height: 8px;
      border-radius: 9999px;
      background: #d4d4d8;
    }
    #${INSERT_SELECTOR_PLACEHOLDER_ID} .storeeel-insert-point-selector-reel,
    .${INSERT_SELECTOR_PLACEHOLDER_CLASS} .storeeel-insert-point-selector-reel {
      /* リール型プレースホルダーは実際の簡単差し込みサイズに合わせる */
      width: 182px;
      height: 324px;
      border-radius: 10px;
      background: #d4d4d8;
      border: 2px solid #a1a1aa;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-confirm {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      margin: 0;
      padding: 12px 14px;
      border: 1px solid #d4d4d8;
      border-radius: 12px;
      background: #ffffff;
      box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
      color: #27272a;
      font-size: 13px;
      line-height: 1.4;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 2147483648;
      box-sizing: border-box;
      width: min(320px, calc(100vw - 16px));
    }
    .storeeel-insert-point-selector-confirm-actions {
      display: inline-flex;
      gap: 8px;
      margin-left: auto;
    }
    .storeeel-insert-point-selector-confirm-body {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .storeeel-insert-point-selector-confirm-status {
      min-height: 18px;
      font-size: 12px;
      line-height: 1.5;
    }
    .storeeel-insert-point-selector-confirm-status[data-status-kind="loading"] {
      color: #52525b;
    }
    .storeeel-insert-point-selector-confirm-status[data-status-kind="success"] {
      color: #166534;
    }
    .storeeel-insert-point-selector-confirm-status[data-status-kind="error"] {
      color: #b91c1c;
    }
    .storeeel-insert-point-selector-confirm-button {
      border: 0;
      border-radius: 9999px;
      padding: 8px 14px;
      font-size: 12px;
      cursor: pointer;
    }
    .storeeel-insert-point-selector-confirm-button-confirm {
      background: #18181b;
      color: #ffffff;
    }
    .storeeel-insert-point-selector-confirm-button-cancel {
      background: #f4f4f5;
      color: #27272a;
    }
    .storeeel-insert-point-selector-menu {
      all: initial;
      --variable-collection-success: rgba(117, 181, 114, 1);
      --variable-collection-success-hover: rgba(110, 167, 107, 1);
      --storeeel-insert-text-primary: rgba(39, 39, 39, 1);
      --storeeel-insert-text-secondary: rgba(102, 102, 102, 1);
      --storeeel-insert-text-muted: rgba(0, 0, 0, 0.6);
      --storeeel-insert-border-strong: rgba(199, 199, 199, 1);
      --storeeel-insert-border-soft: rgba(229, 229, 229, 1);
      position: fixed;
      top: 0;
      right: 0;
      z-index: 2147483647;
      width: min(520px, 100vw);
      height: 100vh;
      overflow: visible;
      pointer-events: none;
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      line-height: normal;
      text-align: left;
      letter-spacing: 0;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-menu *,
    .storeeel-insert-point-selector-menu *::before,
    .storeeel-insert-point-selector-menu *::after {
      all: unset;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-menu-panel {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      gap: 30px;
      padding: 32px 20px;
      width: 100%;
      min-height: 100vh;
      max-height: 100vh;
      overflow: visible;
      box-sizing: border-box;
      background: #ffffff;
      border-left: 1px solid var(--storeeel-insert-border-soft);
      border-radius: 12px 0 0 12px;
      box-shadow: -12px 0 32px rgba(15, 23, 42, 0.10);
      color: var(--storeeel-insert-text-secondary);
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      pointer-events: auto;
      transition: transform 200ms ease-out;
    }
    .storeeel-insert-point-selector-menu-scroll {
      display: flex;
      flex-direction: column;
      gap: 32px;
      width: 100%;
      min-width: 0;
      overflow-y: auto;
      overflow-x: hidden;
      flex: 1 1 auto;
      min-height: 0;
    }
    .storeeel-insert-point-selector-menu-toggle {
      position: absolute;
      left: -32px;
      top: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 56px;
      border: 1px solid rgba(24, 24, 27, 0.18);
      border-right: 0;
      border-radius: 8px 0 0 8px;
      padding: 0;
      font-size: 0;
      cursor: pointer;
      background: rgba(255, 255, 255, 0.92);
      color: #18181b;
      box-shadow:
        -8px 10px 24px rgba(15, 23, 42, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      pointer-events: auto;
      z-index: 2147483648;
    }
    .storeeel-insert-point-selector-menu-toggle::before {
      content: "";
      width: 10px;
      height: 10px;
      border-top: 2px solid currentColor;
      border-right: 2px solid currentColor;
      transform: rotate(45deg);
      opacity: 0.92;
    }
    .storeeel-insert-point-selector-menu-toggle::after {
      content: none;
    }
    .storeeel-insert-point-selector-menu-toggle:focus-visible {
      outline: 3px solid rgba(24, 24, 27, 0.22);
      outline-offset: 3px;
    }
    .storeeel-insert-point-selector-menu[data-storeeel-insert-selector-menu-open="false"] .storeeel-insert-point-selector-menu-toggle::before {
      transform: rotate(-135deg);
    }
    .storeeel-insert-point-selector-menu-content {
      display: flex;
      flex-direction: column;
      gap: 0;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-menu-content[hidden] {
      display: none !important;
    }
    .storeeel-insert-point-selector-menu[data-storeeel-insert-selector-menu-open="false"] .storeeel-insert-point-selector-menu-panel {
      transform: translateX(calc(100% - ${INSERT_SELECTOR_MENU_TOGGLE_VISIBLE_WIDTH}px));
    }
    .storeeel-insert-point-selector-menu-title {
      font-size: 24px;
      font-weight: 700;
      line-height: normal;
      letter-spacing: 0.25px;
      color: var(--storeeel-insert-text-primary);
      margin: 0 0 24px;
    }
    .storeeel-insert-point-selector-menu:not([data-storeeel-insert-selector-menu-mobile-simple="true"]) .storeeel-insert-point-selector-menu-title {
      /* PC簡単設定の大項目は下余白を指定サイズに揃える。 */
      margin-bottom: 12px;
    }
    .storeeel-insert-point-selector-menu-section-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0.1px;
      color: var(--storeeel-insert-text-primary);
      margin: 32px 0 12px;
    }
    .storeeel-insert-point-selector-menu-rank-label {
      display: flex;
      flex-direction: column;
      gap: 8px;
      color: var(--storeeel-insert-text-muted);
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 16.5px;
      letter-spacing: 0;
      margin: 0 0 12px;
    }
    .storeeel-insert-point-selector-status {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin: 0 0 24px;
      color: var(--storeeel-insert-text-muted);
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 16.5px;
    }
    .storeeel-insert-point-selector-status-value {
      color: var(--storeeel-insert-text-primary);
      font-size: 17px;
      font-weight: 400;
      line-height: 23.8px;
    }
    .storeeel-insert-point-selector-menu:not([data-storeeel-insert-selector-menu-mobile-simple="true"]) .storeeel-insert-point-selector-settings-section > .storeeel-insert-point-selector-menu-subsection > .storeeel-insert-point-selector-menu-rank-label,
    .storeeel-insert-point-selector-menu:not([data-storeeel-insert-selector-menu-mobile-simple="true"]) .storeeel-insert-point-selector-settings-section > .storeeel-insert-point-selector-settings-group > .storeeel-insert-point-selector-menu-rank-label {
      /* PC簡単設定の「差し込み位置」「余白」見出しは余白を持たせない。 */
      margin: 0;
    }
    .storeeel-insert-point-selector-menu-options {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding-top: 0;
      margin-top: -8px;
    }
    .storeeel-insert-point-selector-menu-option {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 17px;
      font-weight: 400;
      line-height: 23.8px;
      color: var(--storeeel-insert-text-secondary);
      cursor: pointer;
    }
    .storeeel-insert-point-selector-menu-option input {
      appearance: auto;
      width: 16px;
      height: 16px;
      margin: 0;
      accent-color: #000000;
    }
    .storeeel-insert-point-selector-settings-section {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
      padding: 20px 0 32px;
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-whitespace-section {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-whitespace-body {
      display: inline-flex;
      align-items: center;
      gap: 16px;
      padding-top: 12px;
      background: #ffffff;
      max-width: 100%;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-whitespace-margin {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px;
      position: relative;
      max-width: 100%;
      box-sizing: border-box;
      background: #fff8ac;
      border: 1px dashed var(--storeeel-insert-text-primary);
    }
    .storeeel-insert-point-selector-whitespace-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-whitespace-side {
      align-self: stretch;
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }
    .storeeel-insert-point-selector-whitespace-padding {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px;
      position: relative;
      flex: 1 1 auto;
      min-width: 0;
      max-width: 340px;
      box-sizing: border-box;
      background: #d0eca5;
      border: 1px dashed var(--storeeel-insert-text-primary);
    }
    .storeeel-insert-point-selector-whitespace-inner-row {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      max-width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-whitespace-field {
      display: inline-flex;
      flex-direction: column;
      min-width: 52px;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 4px 6px;
      position: relative;
      flex: 0 0 auto;
      box-sizing: border-box;
      background: #ffffff;
      border: 1px solid var(--storeeel-insert-border-strong);
      border-radius: 6px;
    }
    .storeeel-insert-point-selector-whitespace-field input {
      width: 40px;
      height: 20px;
      border: 0;
      padding: 0;
      background: transparent;
      color: var(--storeeel-insert-text-primary);
      font-family: "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 400;
      line-height: normal;
      text-align: center;
      outline: none;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-whitespace-label {
      position: absolute;
      left: 0;
      top: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 10px 14px;
      color: var(--storeeel-insert-text-primary);
      font-family: "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: normal;
      letter-spacing: 0.25px;
    }
    .storeeel-insert-point-selector-whitespace-icons-circle {
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
      background: #ffffff;
    }
    .storeeel-insert-point-selector-whitespace-circle-item {
      display: flex;
      flex-direction: column;
      width: 40px;
      align-items: center;
      padding: 0 4px;
      box-sizing: border-box;
    }
    .storeeel-insert-point-selector-whitespace-circle {
      width: 24px;
      height: 24px;
      border-radius: 12px;
      background: #a3a3a3;
    }
    .storeeel-insert-point-selector-whitespace-circle-line {
      width: 24px;
      height: 1px;
      margin-top: 5px;
      opacity: 0;
      background: #27272a;
    }
    .storeeel-insert-point-selector-whitespace-icons-square {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex: 0 0 auto;
      padding: 0 6px;
      background: #ffffff;
    }
    .storeeel-insert-point-selector-whitespace-square {
      width: 36px;
      height: 58px;
      border-radius: 4px;
      background: #a3a3a3;
    }
    .storeeel-insert-point-selector-menu-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 24px;
      width: 100%;
    }
    /* タイル型のグループ設定はスマホの簡単設定と同じ2カラム配置で揃える */
    .storeeel-insert-point-selector-menu-grid-cols2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
    }
    .storeeel-insert-point-selector-menu-subheading {
      display: block;
      width: 100%;
      color: var(--storeeel-insert-text-muted);
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 16.5px;
      text-align: left;
    }
    .storeeel-insert-point-selector-menu-subgroup {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-menu-field {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      color: var(--storeeel-insert-text-muted);
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 16.5px;
      min-width: 0;
    }
    .storeeel-insert-point-selector-menu-field input,
    .storeeel-insert-point-selector-menu-field select {
      display: block;
      width: 160px;
      height: 44px;
      box-sizing: border-box;
      border: 1px solid var(--storeeel-insert-border-strong);
      border-radius: 6px;
      padding: 12px;
      font-size: 15px;
      font-weight: 400;
      background: #ffffff;
      color: var(--storeeel-insert-text-primary);
    }
    .storeeel-insert-point-selector-menu-field input[type="radio"] {
      appearance: auto;
      width: auto;
      accent-color: #000000;
    }
    .storeeel-insert-point-selector-menu-field input[type="number"] {
      appearance: auto;
    }
    .storeeel-insert-point-selector-menu-radio-group {
      display: inline-flex;
      align-items: center;
      gap: 32px;
    }
    .storeeel-insert-point-selector-menu-radio-option {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      font-size: 17px;
      font-weight: 400;
      line-height: 23.8px;
      color: var(--storeeel-insert-text-secondary);
      cursor: pointer;
      white-space: nowrap;
    }
    .storeeel-insert-point-selector-menu-radio-option input {
      appearance: auto;
      width: 16px;
      height: 16px;
      margin: 0;
      accent-color: #000000;
      cursor: inherit;
    }
    .storeeel-insert-point-selector-menu-radio-option-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .storeeel-insert-point-selector-menu-subsection {
      display: flex;
      flex-direction: column;
      gap: 24px;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-menu-subsection-middle {
      gap: 0;
    }
    .storeeel-insert-point-selector-menu-subsection-middle > .storeeel-insert-point-selector-menu-section-title {
      margin: 0 0 12px;
    }
    .storeeel-insert-point-selector-settings-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-settings-group + .storeeel-insert-point-selector-settings-group {
      margin-top: 0;
    }
    .storeeel-insert-point-selector-settings-group-body {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
      width: 100%;
      min-width: 0;
    }
    .storeeel-insert-point-selector-menu-separator {
      width: 100%;
      height: 1px;
      background: var(--storeeel-insert-border-soft);
    }
    .storeeel-insert-point-selector-color-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
      color: var(--storeeel-insert-text-muted);
      font-family: "Noto Sans JP", "Noto Sans", Helvetica, Arial, sans-serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 16.5px;
    }
    .storeeel-insert-point-selector-color-controls {
      display: inline-flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      position: relative;
    }
    .storeeel-insert-point-selector-color-swatch {
      width: 122px;
      height: 45px;
      border: 2px solid var(--storeeel-insert-border-strong);
      border-radius: 8px;
      cursor: pointer;
    }
    .storeeel-insert-point-selector-color-field input[type="text"] {
      display: block;
      width: 140px;
      box-sizing: border-box;
      border: 1px solid var(--storeeel-insert-border-strong);
      border-radius: 6px;
      padding: 12px;
      font-size: 15px;
      font-weight: 400;
      color: var(--storeeel-insert-text-primary);
    }
    .storeeel-insert-point-selector-group-name-font input {
      width: 280px;
      padding: 10px 12px;
    }
    .storeeel-insert-point-selector-save-button {
      position: fixed;
      top: 24px;
      right: 24px;
      z-index: 2147483649;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-width: 120px;
      height: 48px;
      border: 0;
      border-radius: 8px;
      padding: 11px;
      box-sizing: border-box;
      font-size: 15px;
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
      background-color: var(--variable-collection-success);
      color: #ffffff;
      transition: background-color 200ms ease-out, color 200ms ease-out, opacity 200ms ease-out;
    }
    .storeeel-insert-point-selector-save-button:hover:not(:disabled) {
      background-color: var(--variable-collection-success-hover);
    }
    .storeeel-insert-point-selector-save-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    @media (max-width: 767px) {
      .storeeel-insert-point-selector-menu {
        width: min(220px, calc(100vw - 24px));
        height: auto;
        min-height: 0;
        max-height: 220px;
        top: auto;
        left: auto;
        right: 12px;
        bottom: 12px;
        pointer-events: auto;
      }
      .storeeel-insert-point-selector-menu-panel {
        position: relative;
        inset: auto;
        width: 100%;
        height: auto;
        min-height: 0;
        max-height: 220px;
        padding: 10px 16px;
        border-radius: 12px;
        border: 1px solid var(--storeeel-insert-border-soft);
        transform: none !important;
        overflow: visible;
      }
      .storeeel-insert-point-selector-menu[data-storeeel-insert-selector-menu-mobile-simple="true"] .storeeel-insert-point-selector-settings-section-mobile-simple {
        padding-top: 10px;
        padding-bottom: 10px;
        gap: 0;
      }
      .storeeel-insert-point-selector-menu-toggle {
        display: none;
      }
      .storeeel-insert-point-selector-menu[data-storeeel-insert-selector-menu-open="false"] .storeeel-insert-point-selector-menu-content {
        display: none;
      }
      .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-selected="true"] {
        /* スマホでも選択済みプレビューは実際の差し込み位置に残す */
        box-sizing: border-box;
        overflow-x: auto;
        justify-content: flex-start;
        align-items: flex-start;
        padding: 16px;
        background: transparent;
      }
      body[data-storeeel-insert-selector-active="true"] .${INSERT_SELECTOR_PLACEHOLDER_CLASS}[data-storeeel-insert-selector-device="mobile"][data-storeeel-insert-selector-frame="reel"][data-storeeel-insert-selector-overlay-target="true"] {
        /* リール型はサムネイルが領域を埋めるため、背景側のハイライトも明示して見え方を安定させる。 */
        background: #ffffff;
        border-radius: 10px;
      }
      .storeeel-insert-point-selector-confirm {
        top: calc(50vh + 8px);
      }
    }
    .storeeel-insert-point-selector-device-warning-backdrop {
      position: fixed;
      inset: 0;
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(24, 24, 27, 0.28);
    }
    .storeeel-insert-point-selector-device-warning {
      width: min(100%, 420px);
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 20px;
      border-radius: 16px;
      background: #ffffff;
      box-shadow: 0 20px 50px rgba(15, 23, 42, 0.20);
      color: #27272a;
    }
    .storeeel-insert-point-selector-device-warning-title {
      font-size: 16px;
      font-weight: 700;
      line-height: 1.4;
    }
    .storeeel-insert-point-selector-device-warning-message {
      font-size: 13px;
      line-height: 1.6;
      color: #3f3f46;
      white-space: pre-line;
    }
    .storeeel-insert-point-selector-device-warning-action {
      display: flex;
      justify-content: flex-end;
    }
    .storeeel-insert-point-selector-device-warning-button {
      border: 0;
      border-radius: 9999px;
      padding: 8px 16px;
      font-size: 12px;
      cursor: pointer;
      background: #18181b;
      color: #ffffff;
    }
  `;
  document.head.appendChild(style);
};

const createInsertSelectorUiRoot = (zIndex, ...children) => {
  const host = document.createElement('div');
  host.setAttribute(INSERT_SELECTOR_UI_ROOT_ATTRIBUTE, 'true');
  host.style.setProperty('all', 'initial', 'important');
  host.style.setProperty('display', 'block', 'important');
  host.style.setProperty('position', 'fixed', 'important');
  host.style.setProperty('inset', '0', 'important');
  host.style.setProperty('z-index', String(zIndex), 'important');
  host.style.setProperty('width', 'auto', 'important');
  host.style.setProperty('height', 'auto', 'important');
  host.style.setProperty('margin', '0', 'important');
  host.style.setProperty('padding', '0', 'important');
  host.style.setProperty('pointer-events', 'none', 'important');
  // 簡単設定の操作UIだけをShadow DOMへ隔離し、プレビュー本体は差し込み位置の通常DOMに残す。
  const shadowRoot = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = document.getElementById('storeeel-insert-point-selector-style')?.textContent || '';
  shadowRoot.appendChild(style);
  children.filter(Boolean).forEach((child) => shadowRoot.appendChild(child));
  return { host, shadowRoot };
};

const ensureInsertSelectorInlineHighlight = (placeholder) => {
  if (!placeholder) return null;
  placeholder
    .querySelector(`:scope > [data-storeeel-insert-selector-highlight="true"]:not([${INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE}="true"])`)
    ?.remove();
  const existingHost = getInsertSelectorHighlightHost(placeholder);
  if (existingHost?.shadowRoot) {
    return existingHost.shadowRoot.querySelector('[data-storeeel-insert-selector-highlight="true"]');
  }

  const host = document.createElement('div');
  host.setAttribute(INSERT_SELECTOR_HIGHLIGHT_HOST_ATTRIBUTE, 'true');
  host.dataset.storeeelInsertSelectorHighlight = 'true';
  host.className = 'storeeel-insert-point-selector-inline-highlight';
  host.style.setProperty('all', 'initial', 'important');
  host.style.setProperty('position', 'absolute', 'important');
  host.style.setProperty('display', 'none', 'important');
  host.style.setProperty('background', '#ffffff', 'important');
  host.style.setProperty('border-radius', '10px', 'important');
  host.style.setProperty('pointer-events', 'none', 'important');
  host.style.setProperty('z-index', '0', 'important');
  host.style.setProperty('box-sizing', 'border-box', 'important');

  const shadowRoot = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = document.getElementById('storeeel-insert-point-selector-style')?.textContent || '';
  const highlight = createInsertSelectorShadowHighlight();
  shadowRoot.appendChild(style);
  shadowRoot.appendChild(highlight);
  placeholder.prepend(host);
  return highlight;
};

// タイル型は行(.storeeel-tile-row)を縦に積むため、中央寄せ用の外側(placeholder)と
// レイアウト用の内側(tile-base)を分離する必要がある。previewHtml を都度この形へ包む。
const wrapPreviewHtmlForFrame = (normalizedFrame, previewHtml) => (
  normalizedFrame === 'tile'
    ? `<div class="${INSERT_SELECTOR_TILE_BASE_CLASS}">${previewHtml}</div>`
    : previewHtml
);

const createPlaceholder = (frame = INSERT_SELECTOR_DEFAULT_FRAME, previewHtml = '') => {
  const normalizedFrame = normalizeInsertPointSelectorFrame(frame);
  const placeholder = document.createElement('div');
  placeholder.id = INSERT_SELECTOR_PLACEHOLDER_ID;
  placeholder.className = INSERT_SELECTOR_PLACEHOLDER_CLASS;
  placeholder.dataset.storeeelInsertSelectorPlaceholder = 'true';
  placeholder.dataset.storeeelInsertSelectorFrame = normalizedFrame;

  if (previewHtml) {
    placeholder.innerHTML = wrapPreviewHtmlForFrame(normalizedFrame, previewHtml);
    ensureInsertSelectorInlineHighlight(placeholder);
    return placeholder;
  }

  ensureInsertSelectorInlineHighlight(placeholder);
  for (let index = 0; index < 3; index += 1) {
    const item = document.createElement('div');
    item.className = 'storeeel-insert-point-selector-item';

    if (normalizedFrame === 'reel') {
      const reel = document.createElement('div');
      reel.className = 'storeeel-insert-point-selector-reel';
      item.appendChild(reel);
    } else {
      const circle = document.createElement('div');
      circle.className = 'storeeel-insert-point-selector-circle';

      const label = document.createElement('div');
      label.className = 'storeeel-insert-point-selector-label';

      item.appendChild(circle);
      item.appendChild(label);
    }
    placeholder.appendChild(item);
  }

  return placeholder;
};

const createInsertSelectorOverlay = () => {
  const overlay = document.createElement('div');
  overlay.className = INSERT_SELECTOR_OVERLAY_CLASS;
  overlay.dataset.storeeelInsertSelectorOverlay = 'true';

  ['top', 'right', 'bottom', 'left'].forEach((partName) => {
    const part = document.createElement('div');
    part.dataset.storeeelInsertSelectorOverlayPart = partName;
    overlay.appendChild(part);
  });
  ['top-left', 'top-right', 'bottom-right', 'bottom-left'].forEach((cornerName) => {
    const corner = document.createElement('div');
    corner.dataset.storeeelInsertSelectorOverlayCorner = cornerName;
    overlay.appendChild(corner);
  });

  return overlay;
};

const createInsertSelectorShadowHighlight = () => {
  const highlight = document.createElement('div');
  highlight.className = 'storeeel-insert-point-selector-shadow-highlight';
  return highlight;
};

const createDeviceWarningPopup = (expectedDevice) => {
  const backdrop = document.createElement('div');
  backdrop.className = 'storeeel-insert-point-selector-device-warning-backdrop';
  backdrop.dataset.storeeelInsertSelectorDeviceWarning = 'true';

  const popup = document.createElement('div');
  popup.className = 'storeeel-insert-point-selector-device-warning';

  const title = document.createElement('div');
  title.className = 'storeeel-insert-point-selector-device-warning-title';
  title.textContent = '表示デバイスが異なります';

  const message = document.createElement('div');
  message.className = 'storeeel-insert-point-selector-device-warning-message';
  message.textContent = expectedDevice === 'mobile'
    ? 'スマホ表示で確認してください\n\n※ Google Chromeのご利用をおすすめします。\n\n1. キーボードの「F12」キーを押します\n2. 画面上部のスマホ・タブレットのアイコンを押します\n3. スマホ表示に切り替わったら、キャンペーンを表示させたい箇所をクリックしてください\n\n切り替え後に「閉じる」を押すと、そのまま操作を続けられます。'
    : 'このURLはPC用です。PCで開いて表示箇所を設定してください。';

  const action = document.createElement('div');
  action.className = 'storeeel-insert-point-selector-device-warning-action';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'storeeel-insert-point-selector-device-warning-button';
  closeButton.dataset.storeeelInsertSelectorDeviceWarningClose = 'true';
  closeButton.textContent = '閉じる';

  action.appendChild(closeButton);
  popup.appendChild(title);
  popup.appendChild(message);
  popup.appendChild(action);
  backdrop.appendChild(popup);

  return { backdrop, closeButton };
};

const insertElementByPosition = (element, target, position) => {
  if (!element || !target) return false;

  switch (position) {
    case 'inner_start':
      target.insertBefore(element, target.firstChild);
      return true;
    case 'inner_end':
      target.appendChild(element);
      return true;
    case 'outer_before':
      if (!target.parentNode) return false;
      target.parentNode.insertBefore(element, target);
      return true;
    case 'outer_after':
    default:
      if (!target.parentNode) return false;
      target.parentNode.insertBefore(element, target.nextSibling);
      return true;
  }
};

const movePlaceholder = (placeholder, target, position) => {
  insertElementByPosition(placeholder, target, position);
};

const createConfirmPopup = () => {
  const popup = document.createElement('div');
  popup.className = 'storeeel-insert-point-selector-confirm';
  popup.dataset.storeeelInsertSelectorConfirm = 'true';

  const body = document.createElement('div');
  body.className = 'storeeel-insert-point-selector-confirm-body';

  const message = document.createElement('div');
  message.textContent = 'ここで確定しますか？';

  const actions = document.createElement('div');
  actions.className = 'storeeel-insert-point-selector-confirm-actions';

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'storeeel-insert-point-selector-confirm-button storeeel-insert-point-selector-confirm-button-cancel';
  cancelButton.dataset.storeeelInsertSelectorCancel = 'true';
  cancelButton.textContent = 'キャンセル';

  const confirmButton = document.createElement('button');
  confirmButton.type = 'button';
  confirmButton.className = 'storeeel-insert-point-selector-confirm-button storeeel-insert-point-selector-confirm-button-confirm';
  confirmButton.dataset.storeeelInsertSelectorConfirmButton = 'true';
  confirmButton.textContent = '確定';

  actions.appendChild(cancelButton);
  actions.appendChild(confirmButton);
  body.appendChild(message);
  popup.appendChild(body);
  popup.appendChild(actions);

  return { popup, confirmButton, cancelButton };
};

const numberOrDefault = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

// 簡単設定のframe(story/reel/tile)から、実際のcampaign.frameへ変換する。
// tileを見落として2値分岐にすると、tile選択時に誤ってcircle型キャンペーンを
// プレビュー対象として探しに行ってしまい、プレビューが表示されなくなる。
export const getCampaignFrameFromSelectorFrame = (frame) => {
  const normalizedFrame = normalizeInsertPointSelectorFrame(frame);
  if (normalizedFrame === 'reel') return 'square';
  if (normalizedFrame === 'tile') return 'tile';
  return 'circle';
};

const normalizeSyncedCampaignSettings = (settings) => {
  const normalizedSettings = { ...settings };
  if (normalizedSettings.autoScrollMode == null && normalizedSettings.auto_scroll_mode != null) {
    normalizedSettings.autoScrollMode = getCampaignAutoScrollMode(normalizedSettings) !== AUTO_SCROLL_MODE.disabled;
  }
  if (normalizedSettings.loopPlaybackEnabled == null && normalizedSettings.loop_playback_enabled != null) {
    normalizedSettings.loopPlaybackEnabled = isCampaignLoopPlaybackEnabled(normalizedSettings);
  }
  if (normalizedSettings.groupGap == null && normalizedSettings.group_gap != null) {
    normalizedSettings.groupGap = normalizedSettings.group_gap;
  }
  if (normalizedSettings.isReadStatusVisible == null && normalizedSettings.is_read_status_visible != null) {
    normalizedSettings.isReadStatusVisible = normalizedSettings.is_read_status_visible === true;
  }
  if (normalizedSettings.readStatusBorderWidth == null && normalizedSettings.read_status_border_width != null) {
    normalizedSettings.readStatusBorderWidth = getCampaignReadStatusBorderWidth(normalizedSettings);
  }
  // タイル型アスペクト比のsnake_case→camelCase変換
  if (normalizedSettings.tileAspectWidth == null && normalizedSettings.tile_aspect_width != null) {
    normalizedSettings.tileAspectWidth = normalizedSettings.tile_aspect_width;
  }
  if (normalizedSettings.tileAspectHeight == null && normalizedSettings.tile_aspect_height != null) {
    normalizedSettings.tileAspectHeight = normalizedSettings.tile_aspect_height;
  }
  // タイル型 行間gapのsnake_case→camelCase変換
  if (normalizedSettings.tileGapVertical == null && normalizedSettings.tile_gap_vertical != null) {
    normalizedSettings.tileGapVertical = normalizedSettings.tile_gap_vertical;
  }
  // タイル型 選択グリッドの列数・行数のsnake_case→camelCase変換（ADR-013）
  if (normalizedSettings.tileColumns == null && normalizedSettings.tile_columns != null) {
    normalizedSettings.tileColumns = normalizedSettings.tile_columns;
  }
  if (normalizedSettings.tileRows == null && normalizedSettings.tile_rows != null) {
    normalizedSettings.tileRows = normalizedSettings.tile_rows;
  }
  return normalizedSettings;
};

export const buildInitialCampaignSettings = (campaign = {}) => ({
  topMargin: numberOrDefault(campaign.topMargin ?? campaign.top_margin, 0),
  rightMargin: numberOrDefault(campaign.rightMargin ?? campaign.right_margin, 0),
  bottomMargin: numberOrDefault(campaign.bottomMargin ?? campaign.bottom_margin, 0),
  leftMargin: numberOrDefault(campaign.leftMargin ?? campaign.left_margin, 0),
  topPadding: numberOrDefault(campaign.topPadding ?? campaign.top_padding, 0),
  rightPadding: numberOrDefault(campaign.rightPadding ?? campaign.right_padding, 0),
  bottomPadding: numberOrDefault(campaign.bottomPadding ?? campaign.bottom_padding, 0),
  leftPadding: numberOrDefault(campaign.leftPadding ?? campaign.left_padding, 0),
  autoScrollMode: getCampaignAutoScrollMode(campaign) !== AUTO_SCROLL_MODE.disabled,
  loopPlaybackEnabled: isCampaignLoopPlaybackEnabled(campaign),
  isReadStatusVisible: campaign.isReadStatusVisible === true || campaign.is_read_status_visible === true,
  unreadColorCode: campaign.unreadColorCode ?? campaign.unread_color_code ?? '#000000',
  readColorCode: campaign.readColorCode ?? campaign.read_color_code ?? '#CCCCCC',
  readStatusBorderWidth: getCampaignReadStatusBorderWidth(campaign),
  thumbnailScale: numberOrDefault(campaign.thumbnailScale ?? campaign.thumbnail_scale, 1),
  groupGap: numberOrDefault(campaign.groupGap ?? campaign.group_gap, campaign.frame === 'circle' ? 22 : 10),
  groupNameFontSize: numberOrDefault(campaign.groupNameFontSize ?? campaign.group_name_font_size, 12),
  groupNameFontWeight: numberOrDefault(campaign.groupNameFontWeight ?? campaign.group_name_font_weight, 400),
  groupNameColorCode: campaign.groupNameColorCode ?? campaign.group_name_color_code ?? '#272727',
  groupNameFontFamily: campaign.groupNameFontFamily ?? campaign.group_name_font_family ?? '',
  // タイル型アスペクト比（デフォルト 9/16）
  tileAspectWidth: numberOrDefault(campaign.tileAspectWidth ?? campaign.tile_aspect_width, 9),
  tileAspectHeight: numberOrDefault(campaign.tileAspectHeight ?? campaign.tile_aspect_height, 16),
  // タイル型 行間の余白（デフォルト 10px）
  tileGapVertical: numberOrDefault(campaign.tileGapVertical ?? campaign.tile_gap_vertical, 10),
  // タイル型 選択グリッドの列数・行数（ADR-013、デフォルト 4列 x 2行）
  tileColumns: numberOrDefault(campaign.tileColumns ?? campaign.tile_columns, 4),
  tileRows: numberOrDefault(campaign.tileRows ?? campaign.tile_rows, 2),
});

const applyTilePlaceholderLayoutSettings = (placeholder, settings) => {
  // 外側(placeholder)は中央寄せの役割のみを持つ。margin/padding・行間gap・
  // グリッド全体の最大幅は内側(tile-base)へ適用し、本番の div > .storeeell_base_X と同じ見た目にする。
  placeholder.style.margin = '';
  placeholder.style.padding = '0';
  placeholder.style.gap = '0';
  placeholder.style.alignItems = 'flex-start';
  placeholder.style.overflowX = 'visible';
  placeholder.style.whiteSpace = 'normal';
  placeholder.dataset.storeeelInsertSelectorAutoScroll = 'false';
  syncPlaceholderPreviewAlignment(placeholder);

  const tileBase = placeholder.querySelector(`.${INSERT_SELECTOR_TILE_BASE_CLASS}`);
  if (!tileBase) return;
  tileBase.style.display = 'flex';
  tileBase.style.flexDirection = 'column';
  tileBase.style.boxSizing = 'border-box';
  tileBase.style.width = '100%';
  tileBase.style.gap = `${getCampaignTileGapVertical(settings)}px`;
  tileBase.style.maxWidth = `min(100%, ${getCampaignTileBlockMaxWidth(settings)}px)`;
  tileBase.style.margin = `${settings.topMargin}px ${settings.rightMargin}px ${settings.bottomMargin}px ${settings.leftMargin}px`;
  tileBase.style.padding = `${settings.topPadding}px ${settings.rightPadding}px ${settings.bottomPadding}px ${settings.leftPadding}px`;
};

const applyPlaceholderLayoutSettings = (placeholder, settings, frame = INSERT_SELECTOR_DEFAULT_FRAME) => {
  if (!placeholder) return;
  const campaignFrame = getCampaignFrameFromSelectorFrame(frame);
  placeholder.dataset.storeeelInsertSelectorLoopPlayback = String(Boolean(settings.loopPlaybackEnabled));
  stopPlaceholderPreviewEffects(placeholder);

  if (campaignFrame === 'tile') {
    applyTilePlaceholderLayoutSettings(placeholder, settings);
    return;
  }

  const renderedGroupGap = getRenderedCampaignGroupGap({
    frame: campaignFrame,
    groupGap: settings.groupGap,
    isReadStatusVisible: settings.isReadStatusVisible,
  });
  placeholder.style.margin = `${settings.topMargin}px ${settings.rightMargin}px ${settings.bottomMargin}px ${settings.leftMargin}px`;
  placeholder.style.padding = `${settings.topPadding}px ${settings.rightPadding}px ${settings.bottomPadding}px ${settings.leftPadding}px`;
  placeholder.style.alignItems = campaignFrame === 'circle' ? 'flex-start' : 'center';
  syncPlaceholderPreviewAlignment(placeholder);
  if (renderedGroupGap != null) {
    // 実表示の閉じた状態と同じ計算で、プレビューのグループ間隔を揃える。
    placeholder.style.gap = `${renderedGroupGap}px`;
  } else {
    placeholder.style.gap = campaignFrame === 'circle' ? '8px' : '';
  }
  placeholder.dataset.storeeelInsertSelectorAutoScroll = String(Boolean(settings.autoScrollMode));
  if (!settings.autoScrollMode) return;

  // 簡単設定の横スクロールは、本番と同じスクロール制御を使って挙動差をなくす。
  placeholder.storeeelInsertSelectorAutoScrollCleanup = enableCampaignAutoScroll(placeholder, {
    loop: true,
    renderMode: window.isIphone ? 'transform' : 'scroll',
  });
};

const reapplyPlaceholderGroupGap = (placeholder, settings, frame = INSERT_SELECTOR_DEFAULT_FRAME) => {
  if (!placeholder) return;
  const campaignFrame = getCampaignFrameFromSelectorFrame(frame);
  // タイル型に自動横スクロールは無く、gapの再適用は applyPlaceholderLayoutSettings 側で完結する。
  if (campaignFrame === 'tile') return;
  const groupGap = Number(settings.groupGap);
  const renderedGroupGap = getRenderedCampaignGroupGap({
    frame: campaignFrame,
    groupGap: settings.groupGap,
    isReadStatusVisible: settings.isReadStatusVisible,
  });
  const nextGap = renderedGroupGap != null ? `${renderedGroupGap}px` : (campaignFrame === 'circle' ? '8px' : '');
  placeholder.style.gap = nextGap;
  placeholder.querySelector('.storeeel-auto-scroll-track')?.style?.setProperty('gap', nextGap);
  if (campaignFrame === 'square' && Number.isFinite(groupGap)) {
    const contentHorizontalMargin = Math.min(groupGap / 2, 5);
    placeholder.querySelectorAll('.storeeel-content').forEach((content) => {
      content.style.marginLeft = `${contentHorizontalMargin}px`;
      content.style.marginRight = `${contentHorizontalMargin}px`;
    });
  }
};

const syncPlaceholderPreviewAlignment = (placeholder) => {
  const applyAlignment = () => {
    if (!placeholder) return;
    // 簡単設定のスマホ表示は左寄せを維持し、それ以外は横スクロール不要時のみ中央寄せにする。
    if (getInsertPointSelectorDevice() === 'mobile') {
      placeholder.style.justifyContent = 'flex-start';
      return;
    }
    const hasHorizontalOverflow = placeholder.scrollWidth > placeholder.clientWidth + 1;
    placeholder.style.justifyContent = hasHorizontalOverflow ? 'flex-start' : 'center';
  };

  applyAlignment();
  window.requestAnimationFrame?.(applyAlignment);
};

const syncPlaceholderDragHandlePosition = (placeholder, dragHandle) => {
  if (!placeholder?.dataset || placeholder.dataset.storeeelInsertSelectorSelected !== 'true' || !dragHandle) return;
  const placeholderRect = placeholder.getBoundingClientRect();
  const campaigns = Array.from(placeholder.querySelectorAll('.storeeel_campaign'))
    .map((campaign) => campaign.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);
  if (campaigns.length === 0) {
    dragHandle.style.display = 'none';
    return;
  }

  dragHandle.style.display = 'block';
  const campaignTop = Math.min(...campaigns.map((rect) => rect.top));
  // 横スクロールしても、ハンドルは常に現在見えている領域の中央に固定する。
  const handleLeft = placeholderRect.left + (placeholder.clientWidth / 2) - 25;
  const handleTop = campaignTop - 24;
  dragHandle.style.left = `${Math.round(handleLeft)}px`;
  dragHandle.style.top = `${Math.round(handleTop)}px`;
};

const stopPlaceholderPreviewEffects = (placeholder) => {
  if (typeof placeholder?.storeeelInsertSelectorAutoScrollCleanup === 'function') {
    placeholder.storeeelInsertSelectorAutoScrollCleanup();
    placeholder.storeeelInsertSelectorAutoScrollCleanup = null;
  }
  if (placeholder?.storeeelInsertSelectorAutoScrollTimer) {
    window.clearInterval(placeholder.storeeelInsertSelectorAutoScrollTimer);
    placeholder.storeeelInsertSelectorAutoScrollTimer = null;
  }
  if (placeholder?.dataset) {
    delete placeholder.dataset.storeeelAutoScrollLoopPrepared;
    delete placeholder.dataset.storeeelAutoScrollLoopWidth;
    delete placeholder.dataset.storeeelAutoScrollPosition;
    delete placeholder.dataset.storeeelAutoScrollRenderMode;
  }
};

const appendNumberField = (parent, labelText, value, onChange, options = {}) => {
  const label = document.createElement('label');
  label.className = 'storeeel-insert-point-selector-menu-field';
  const text = document.createElement('span');
  text.textContent = labelText;
  const input = document.createElement('input');
  input.type = 'number';
  input.value = String(value);
  if (options.min != null) input.min = String(options.min);
  if (options.max != null) input.max = String(options.max);
  if (options.step != null) input.step = String(options.step);
  input.addEventListener('input', () => onChange(Number(input.value)));
  label.appendChild(text);
  label.appendChild(input);
  parent.appendChild(label);
  return input;
};

const appendWhitespaceNumberField = (parent, labelText, value, onChange) => {
  const label = document.createElement('label');
  label.className = 'storeeel-insert-point-selector-whitespace-field';
  const input = document.createElement('input');
  input.type = 'number';
  input.value = String(value);
  input.placeholder = '00';
  input.setAttribute('aria-label', labelText);
  input.addEventListener('input', () => onChange(Number(input.value)));
  label.appendChild(input);
  parent.appendChild(label);
  return input;
};

const createWhitespaceIcons = (isReelFrame) => {
  const wrapper = document.createElement('div');
  wrapper.className = isReelFrame
    ? 'storeeel-insert-point-selector-whitespace-icons-square'
    : 'storeeel-insert-point-selector-whitespace-icons-circle';

  const count = isReelFrame ? 3 : 4;
  for (let index = 0; index < count; index += 1) {
    if (isReelFrame) {
      const square = document.createElement('div');
      square.className = 'storeeel-insert-point-selector-whitespace-square';
      wrapper.appendChild(square);
      continue;
    }

    const item = document.createElement('div');
    item.className = 'storeeel-insert-point-selector-whitespace-circle-item';
    const circle = document.createElement('div');
    circle.className = 'storeeel-insert-point-selector-whitespace-circle';
    const line = document.createElement('div');
    line.className = 'storeeel-insert-point-selector-whitespace-circle-line';
    item.appendChild(circle);
    item.appendChild(line);
    wrapper.appendChild(item);
  }

  return wrapper;
};

const createWhitespaceSection = (settings, onSettingChange, isReelFrame) => {
  const section = document.createElement('div');
  section.className = 'storeeel-insert-point-selector-whitespace-section';

  const body = document.createElement('div');
  body.className = 'storeeel-insert-point-selector-whitespace-body';

  const marginBox = document.createElement('div');
  marginBox.className = 'storeeel-insert-point-selector-whitespace-margin';

  appendWhitespaceNumberField(marginBox, 'margin 上', settings.topMargin, (value) => onSettingChange('topMargin', value));

  const row = document.createElement('div');
  row.className = 'storeeel-insert-point-selector-whitespace-row';

  const leftMargin = document.createElement('div');
  leftMargin.className = 'storeeel-insert-point-selector-whitespace-side';
  appendWhitespaceNumberField(leftMargin, 'margin 左', settings.leftMargin, (value) => onSettingChange('leftMargin', value));

  const paddingBox = document.createElement('div');
  paddingBox.className = 'storeeel-insert-point-selector-whitespace-padding';
  appendWhitespaceNumberField(paddingBox, 'padding 上', settings.topPadding, (value) => onSettingChange('topPadding', value));

  const innerRow = document.createElement('div');
  innerRow.className = 'storeeel-insert-point-selector-whitespace-inner-row';
  appendWhitespaceNumberField(innerRow, 'padding 左', settings.leftPadding, (value) => onSettingChange('leftPadding', value));
  innerRow.appendChild(createWhitespaceIcons(isReelFrame));
  appendWhitespaceNumberField(innerRow, 'padding 右', settings.rightPadding, (value) => onSettingChange('rightPadding', value));
  paddingBox.appendChild(innerRow);

  appendWhitespaceNumberField(paddingBox, 'padding 下', settings.bottomPadding, (value) => onSettingChange('bottomPadding', value));

  const paddingLabel = document.createElement('div');
  paddingLabel.className = 'storeeel-insert-point-selector-whitespace-label';
  paddingLabel.textContent = 'padding';
  paddingBox.appendChild(paddingLabel);

  const rightMargin = document.createElement('div');
  rightMargin.className = 'storeeel-insert-point-selector-whitespace-side';
  appendWhitespaceNumberField(rightMargin, 'margin 右', settings.rightMargin, (value) => onSettingChange('rightMargin', value));

  row.appendChild(leftMargin);
  row.appendChild(paddingBox);
  row.appendChild(rightMargin);
  marginBox.appendChild(row);

  appendWhitespaceNumberField(marginBox, 'margin 下', settings.bottomMargin, (value) => onSettingChange('bottomMargin', value));

  const marginLabel = document.createElement('div');
  marginLabel.className = 'storeeel-insert-point-selector-whitespace-label';
  marginLabel.textContent = 'margin';
  marginBox.appendChild(marginLabel);

  body.appendChild(marginBox);
  section.appendChild(body);
  return section;
};

const normalizeHexColor = (value, fallback = '#000000') => {
  const normalizedValue = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(normalizedValue)) return normalizedValue.toUpperCase();
  if (/^[0-9a-f]{6}$/i.test(normalizedValue)) return `#${normalizedValue.toUpperCase()}`;
  return fallback;
};

const appendTextField = (parent, labelText, value, onChange) => {
  const label = document.createElement('label');
  label.className = 'storeeel-insert-point-selector-menu-field';
  const text = document.createElement('span');
  text.textContent = labelText;
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value || '';
  input.addEventListener('input', () => onChange(input.value));
  label.appendChild(text);
  label.appendChild(input);
  parent.appendChild(label);
  return input;
};

const appendColorField = (parent, labelText, value, onChange) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'storeeel-insert-point-selector-color-field';
  const text = document.createElement('span');
  text.textContent = labelText;

  const controls = document.createElement('div');
  controls.className = 'storeeel-insert-point-selector-color-controls';

  const swatch = document.createElement('button');
  swatch.type = 'button';
  swatch.className = 'storeeel-insert-point-selector-color-swatch';
  swatch.tabIndex = -1;
  swatch.setAttribute('aria-hidden', 'true');

  const colorInput = document.createElement('input');
  colorInput.type = 'color';
  colorInput.value = normalizeHexColor(value);
  colorInput.setAttribute('aria-hidden', 'true');
  colorInput.tabIndex = -1;
  colorInput.style.position = 'absolute';
  colorInput.style.left = '0';
  colorInput.style.top = '0';
  colorInput.style.width = '1px';
  colorInput.style.height = '1px';
  colorInput.style.opacity = '0';
  colorInput.style.pointerEvents = 'none';

  const input = document.createElement('input');
  input.type = 'text';
  input.setAttribute('aria-label', labelText);
  input.value = colorInput.value;
  swatch.style.backgroundColor = input.value;

  const syncColor = (nextValue) => {
    const normalizedValue = normalizeHexColor(nextValue);
    input.value = normalizedValue;
    colorInput.value = normalizedValue;
    swatch.style.backgroundColor = normalizedValue;
    onChange(normalizedValue);
  };

  input.addEventListener('change', () => syncColor(input.value));
  input.addEventListener('input', () => {
    if (!/^#[0-9a-f]{6}$/i.test(input.value)) return;
    syncColor(input.value);
  });
  colorInput.addEventListener('input', () => syncColor(colorInput.value));
  colorInput.addEventListener('change', () => syncColor(colorInput.value));
  swatch.addEventListener('click', () => {
    if (typeof colorInput.showPicker === 'function') {
      colorInput.showPicker();
      return;
    }
    colorInput.click();
  });

  controls.appendChild(swatch);
  controls.appendChild(colorInput);
  controls.appendChild(input);
  wrapper.appendChild(text);
  wrapper.appendChild(controls);
  parent.appendChild(wrapper);
  return input;
};

const appendRadioSetting = (parent, labelText, checked, onChange, options = {}) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'storeeel-insert-point-selector-menu-subsection';
  if (options.titleLevel === 'middle') {
    wrapper.classList.add('storeeel-insert-point-selector-menu-subsection-middle');
  }

  const title = createMenuSectionHeading(labelText, options.titleLevel ?? 'small');

  const group = document.createElement('fieldset');
  group.className = 'storeeel-insert-point-selector-menu-radio-group';
  group.setAttribute('aria-label', labelText);
  group.setAttribute('role', 'radiogroup');

  const name = `storeeel-${labelText}-${Math.random().toString(36).slice(2, 8)}`;
  const inputs = {};
  [
    { value: true, label: 'ON', disabled: false },
    { value: false, label: 'OFF', disabled: options.disableOff === true },
  ].forEach(({ value, label, disabled }) => {
    const option = document.createElement('label');
    option.className = 'storeeel-insert-point-selector-menu-radio-option';
    if (disabled) option.classList.add('storeeel-insert-point-selector-menu-radio-option-disabled');

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = name;
    input.value = value ? 'on' : 'off';
    input.checked = Boolean(checked) === value;
    input.disabled = disabled;
    input.addEventListener('change', () => {
      if (!input.checked || input.disabled) return;
      onChange(value);
    });

    const text = document.createElement('span');
    text.textContent = label;
    option.appendChild(input);
    option.appendChild(text);
    group.appendChild(option);
    inputs[value ? 'on' : 'off'] = input;
  });

  wrapper.appendChild(title);
  wrapper.appendChild(group);
  parent.appendChild(wrapper);
  return inputs;
};

const appendPositionSetting = (parent, selectedPosition, onChange, options = {}) => {
  const wrapper = document.createElement('div');
  wrapper.className = 'storeeel-insert-point-selector-menu-subsection';

  const showTitle = options.showTitle !== false;
  if (showTitle) {
    const title = document.createElement('div');
    title.className = 'storeeel-insert-point-selector-menu-rank-label';
    title.textContent = '差し込み位置';
    wrapper.appendChild(title);
  }

  const optionList = document.createElement('div');
  optionList.className = 'storeeel-insert-point-selector-menu-options';

  INSERT_SELECTOR_POSITIONS.forEach(({ value, label }) => {
    const option = document.createElement('label');
    option.className = 'storeeel-insert-point-selector-menu-option';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'storeeel-insert-position';
    input.value = value;
    input.checked = value === selectedPosition;
    input.addEventListener('change', () => {
      if (!input.checked) return;
      onChange?.(value);
    });

    const text = document.createElement('span');
    text.textContent = label;

    option.appendChild(input);
    option.appendChild(text);
    optionList.appendChild(option);
  });

  wrapper.appendChild(optionList);
  parent.appendChild(wrapper);
};

const createInsertSelectorStatus = (initialText = '配置モード') => {
  const status = document.createElement('div');
  status.className = 'storeeel-insert-point-selector-status';
  status.dataset.storeeelInsertSelectorStatus = 'true';

  const label = document.createElement('span');
  label.textContent = 'ステータス';

  const value = document.createElement('span');
  value.className = 'storeeel-insert-point-selector-status-value';
  value.dataset.storeeelInsertSelectorStatusValue = 'true';
  value.textContent = initialText;

  status.appendChild(label);
  status.appendChild(value);
  return status;
};

const appendSelectField = (parent, labelText, value, values, onChange) => {
  const label = document.createElement('label');
  label.className = 'storeeel-insert-point-selector-menu-field';
  const text = document.createElement('span');
  text.textContent = labelText;
  const select = document.createElement('select');
  values.forEach((optionValue) => {
    const option = document.createElement('option');
    option.value = String(optionValue);
    option.textContent = String(optionValue);
    select.appendChild(option);
  });
  select.value = String(value);
  select.addEventListener('change', () => onChange(Number(select.value)));
  label.appendChild(text);
  label.appendChild(select);
  parent.appendChild(label);
  return select;
};

const createMenuSectionHeading = (text, level = 'small') => {
  const title = document.createElement('div');
  if (level === 'major') {
    title.className = 'storeeel-insert-point-selector-menu-title';
  } else if (level === 'middle') {
    title.className = 'storeeel-insert-point-selector-menu-section-title';
  } else {
    title.className = 'storeeel-insert-point-selector-menu-rank-label';
  }
  title.textContent = text;
  return title;
};

const createSettingsGroup = (titleText, level, bodyElement) => {
  const group = document.createElement('section');
  group.className = 'storeeel-insert-point-selector-settings-group';
  group.appendChild(createMenuSectionHeading(titleText, level));
  group.appendChild(bodyElement);
  return group;
};

const createOuterFrameSettings = (settings, onSettingChange, getCurrentSettings = () => settings) => {
  const section = document.createElement('div');
  section.className = 'storeeel-insert-point-selector-settings-group-body';
  const getLatestSettings = () => getCurrentSettings?.() || settings;

  let syncReadStatusColorFields = () => {};
  appendRadioSetting(section, '未視聴・視聴済みの表示', settings.isReadStatusVisible, (value) => {
    onSettingChange('isReadStatusVisible', value);
    // 外枠の切り替え直後にグループ余白を再計算し、表示崩れを防ぐ。
    onSettingChange('groupGap', getLatestSettings().groupGap);
    syncReadStatusColorFields(value);
  });

  const colorGrid = document.createElement('div');
  colorGrid.className = 'storeeel-insert-point-selector-menu-grid';
  appendNumberField(colorGrid, '外枠の太さ', settings.readStatusBorderWidth, (value) => onSettingChange('readStatusBorderWidth', value), { min: 1, max: 20 });
  appendColorField(colorGrid, '未視聴カラー', settings.unreadColorCode, (value) => onSettingChange('unreadColorCode', value));
  appendColorField(colorGrid, '視聴済みカラー', settings.readColorCode, (value) => onSettingChange('readColorCode', value));

  syncReadStatusColorFields = (isVisible) => {
    colorGrid.style.display = isVisible ? '' : 'none';
  };

  section.appendChild(colorGrid);
  syncReadStatusColorFields(settings.isReadStatusVisible);
  return section;
};

const createGroupSettings = (settings, onSettingChange, isReelFrame) => {
  const section = document.createElement('div');
  section.className = 'storeeel-insert-point-selector-settings-group-body';

  const groupGrid = document.createElement('div');
  groupGrid.className = 'storeeel-insert-point-selector-menu-grid';
  appendNumberField(
    groupGrid,
    isReelFrame ? 'サムネイルの大きさの倍率' : 'グループの大きさの倍率',
    settings.thumbnailScale,
    (value) => onSettingChange('thumbnailScale', value),
    { min: 0.5, max: 2, step: 0.1 }
  );
  appendNumberField(groupGrid, 'グループ余白', settings.groupGap, (value) => onSettingChange('groupGap', value), { min: 0, max: 100 });
  if (!isReelFrame) {
    appendNumberField(groupGrid, '文字サイズ', settings.groupNameFontSize, (value) => onSettingChange('groupNameFontSize', value), { min: 8, max: 32 });
    appendSelectField(groupGrid, '文字の太さ', settings.groupNameFontWeight, [100, 200, 300, 400, 500, 600, 700, 800, 900], (value) => onSettingChange('groupNameFontWeight', value));
    appendColorField(groupGrid, 'グループ名の色', settings.groupNameColorCode, (value) => onSettingChange('groupNameColorCode', value));
  }
  section.appendChild(groupGrid);

  if (!isReelFrame) {
    const fontInput = appendTextField(section, 'グループ名のフォント', settings.groupNameFontFamily, (value) => onSettingChange('groupNameFontFamily', value));
    fontInput.closest('label')?.classList.add('storeeel-insert-point-selector-group-name-font');
  }

  return section;
};

// タイル型の「グループの設定」。管理画面の配信＞カスタムタブと項目を揃える
// （横の余白/縦の余白/縦横比率のみ。大きさの倍率はADR-014で廃止、グループ名系はタイルに存在しない）。
// レイアウトはスマホの簡単設定と同じ「2カラムの余白 → 縦横比率見出し → 2カラムの幅/高さ」で揃える。
const createTileGroupSettings = (settings, onSettingChange) => {
  const section = document.createElement('div');
  section.className = 'storeeel-insert-point-selector-settings-group-body';

  const gapGrid = document.createElement('div');
  gapGrid.className = 'storeeel-insert-point-selector-menu-grid storeeel-insert-point-selector-menu-grid-cols2';
  appendNumberField(gapGrid, '横の余白', settings.groupGap, (value) => onSettingChange('groupGap', value), { min: 0, max: 100 });
  appendNumberField(gapGrid, '縦の余白', settings.tileGapVertical, (value) => onSettingChange('tileGapVertical', value), { min: 0, max: 100 });
  section.appendChild(gapGrid);

  const aspectRatioSection = document.createElement('div');
  aspectRatioSection.className = 'storeeel-insert-point-selector-menu-subgroup';

  const aspectRatioHeading = document.createElement('div');
  aspectRatioHeading.className = 'storeeel-insert-point-selector-menu-subheading';
  aspectRatioHeading.textContent = 'コンテンツの縦横比率';
  aspectRatioSection.appendChild(aspectRatioHeading);

  const aspectRatioGrid = document.createElement('div');
  aspectRatioGrid.className = 'storeeel-insert-point-selector-menu-grid storeeel-insert-point-selector-menu-grid-cols2';
  // 縦横比率はAPIバリデーション（TILE_ASPECT_RANGE = 1..32）・管理画面と同じ 1〜32 に揃える。
  appendNumberField(aspectRatioGrid, '幅', settings.tileAspectWidth, (value) => onSettingChange('tileAspectWidth', value), { min: 1, max: 32 });
  appendNumberField(aspectRatioGrid, '高さ', settings.tileAspectHeight, (value) => onSettingChange('tileAspectHeight', value), { min: 1, max: 32 });
  aspectRatioSection.appendChild(aspectRatioGrid);
  section.appendChild(aspectRatioSection);

  return section;
};

const createSettingsSection = (
  selectedPosition,
  settings,
  onPositionChange,
  onSettingChange,
  onSave,
  showSelectionStatus,
  frame = INSERT_SELECTOR_DEFAULT_FRAME,
  getCurrentSettings = () => settings
) => {
  const isReelFrame = normalizeInsertPointSelectorFrame(frame) === 'reel';
  const isTileFrame = normalizeInsertPointSelectorFrame(frame) === 'tile';
  const isMobileSimpleMenu = getInsertPointSelectorDevice() === 'mobile';
  const section = document.createDocumentFragment();
  const getLatestSettings = () => getCurrentSettings?.() || settings;

  if (!isMobileSimpleMenu) {
    section.appendChild(createMenuSectionHeading('位置の指定', 'major'));
  }

  const positionCardSection = document.createElement('div');
  positionCardSection.className = 'storeeel-insert-point-selector-settings-section';
  if (isMobileSimpleMenu) {
    positionCardSection.classList.add('storeeel-insert-point-selector-settings-section-mobile-simple');
  }
  if (!isMobileSimpleMenu) {
    positionCardSection.appendChild(createInsertSelectorStatus());
  }
  appendPositionSetting(positionCardSection, selectedPosition, onPositionChange, {
    showTitle: !isMobileSimpleMenu,
  });
  if (!isMobileSimpleMenu) {
    positionCardSection.appendChild(createSettingsGroup(
      '余白',
      'small',
      createWhitespaceSection(settings, onSettingChange, isReelFrame || isTileFrame)
    ));
  }
  section.appendChild(positionCardSection);
  if (!isMobileSimpleMenu && onSave && showSelectionStatus) {
    const selectionStatusButton = createSelectionStatusSection(onSave, true);
    if (selectionStatusButton) section.appendChild(selectionStatusButton);
  }

  if (isMobileSimpleMenu) {
    return section;
  }

  section.appendChild(createMenuSectionHeading('カスタム', 'major'));

  const cardSection = document.createElement('div');
  cardSection.className = 'storeeel-insert-point-selector-settings-section';
  let loopPlaybackInputs = null;
  // タイル型は横スクロールしない固定グリッドのため、自動横スクロールの設定自体を持たない。
  if (!isTileFrame) {
    appendRadioSetting(cardSection, '自動横スクロール', settings.autoScrollMode, (value) => {
      onSettingChange('autoScrollMode', value);
      // 横スクロールの切り替え後に、最新のグループ余白を再適用する。
      onSettingChange('groupGap', getLatestSettings().groupGap);
      if (value) {
        if (loopPlaybackInputs) {
          loopPlaybackInputs.on.checked = true;
          loopPlaybackInputs.off.disabled = true;
          loopPlaybackInputs.off.parentElement.classList.add('storeeel-insert-point-selector-menu-radio-option-disabled');
        }
        onSettingChange('loopPlaybackEnabled', true);
      } else if (loopPlaybackInputs) {
        loopPlaybackInputs.off.disabled = false;
        loopPlaybackInputs.off.parentElement.classList.remove('storeeel-insert-point-selector-menu-radio-option-disabled');
      }
    }, { titleLevel: 'middle' });
  }
  loopPlaybackInputs = appendRadioSetting(
    cardSection,
    'ループ再生',
    settings.autoScrollMode ? true : settings.loopPlaybackEnabled,
    (value) => onSettingChange('loopPlaybackEnabled', value),
    { disableOff: !isTileFrame && settings.autoScrollMode, titleLevel: 'middle' }
  );

  const items = [];
  // 外枠(未視聴/既読の枠色)はcircle型専用の設定のため、reel/tileでは表示しない。
  if (!isReelFrame && !isTileFrame) {
    items.push({
      title: '外枠の設定',
      level: 'middle',
      body: createOuterFrameSettings(settings, onSettingChange, getCurrentSettings),
    });
  }

  items.push({
    title: isReelFrame ? 'サムネイルの設定' : 'グループの設定',
    level: 'middle',
    body: isTileFrame
      ? createTileGroupSettings(settings, onSettingChange)
      : createGroupSettings(settings, onSettingChange, isReelFrame),
  });

  items.forEach((item) => {
    cardSection.appendChild(createSettingsGroup(item.title, item.level, item.body));
  });

  section.appendChild(cardSection);
  return section;
};

const showInsertPointSelectorToast = (message, variant = 'default') => {
  // 簡単設定では保存成功とエラーだけを通知し、進行中の青色トーストは表示しない。
  if (!['success', 'error'].includes(variant)) return;

  if (getInsertPointSelectorDevice() === 'mobile') {
    document.querySelector('[data-storeeel-insert-selector-toast="true"]')
      ?.closest('.Toastify')
      ?.remove();
    return;
  }

  showToast({
    message,
    variant,
    duration: 6000,
    dataAttributeName: 'data-storeeel-insert-selector-toast',
    dataAttributeValue: 'true',
  });
};

const createSelectionStatusSection = (onSave, showSaveButton = false) => {
  if (!showSaveButton) return null;

  const saveButton = document.createElement('button');
  saveButton.type = 'button';
  // 簡単設定の保存ボタンは、指定の success 配色に固定する。
  saveButton.className = 'storeeel-insert-point-selector-save-button';
  saveButton.textContent = '保存';
  saveButton.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();
    saveButton.disabled = true;
    try {
      await onSave?.();
      showInsertPointSelectorToast('保存しました', 'success');
    } catch (error) {
      showInsertPointSelectorToast(error?.message || '保存に失敗しました。', 'error');
    } finally {
      saveButton.disabled = false;
    }
  });
  return saveButton;
};

const createPositionMenu = (
  selectedPosition,
  onChange,
  settings,
  onSettingChange,
  onSave,
  frame = INSERT_SELECTOR_DEFAULT_FRAME,
  getCurrentSettings = () => settings
) => {
  const menu = document.createElement('div');
  menu.className = 'storeeel-insert-point-selector-menu';
  menu.dataset.storeeelInsertSelectorMenu = 'true';
  menu.dataset.storeeelInsertSelectorMenuOpen = 'true';
  if (getInsertPointSelectorDevice() === 'mobile') {
    menu.dataset.storeeelInsertSelectorMenuMobileSimple = 'true';
  }
  let isMenuOpen = true;

  const panel = document.createElement('div');
  panel.className = 'storeeel-insert-point-selector-menu-panel';
  panel.style.transform = 'translateX(0)';

  const toggleButton = document.createElement('button');
  toggleButton.type = 'button';
  toggleButton.className = 'storeeel-insert-point-selector-menu-toggle';
  toggleButton.dataset.storeeelInsertSelectorMenuToggle = 'true';
  toggleButton.textContent = 'メニューを閉じる';
  toggleButton.setAttribute('aria-expanded', 'true');
  toggleButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    isMenuOpen = !isMenuOpen;
    menu.dataset.storeeelInsertSelectorMenuOpen = String(isMenuOpen);
    content.hidden = !isMenuOpen;
    panel.style.transform = isMenuOpen
      ? 'translateX(0)'
      : `translateX(calc(100% - ${INSERT_SELECTOR_MENU_TOGGLE_VISIBLE_WIDTH}px))`;
    toggleButton.textContent = isMenuOpen ? 'メニューを閉じる' : 'メニューを開く';
    toggleButton.setAttribute('aria-expanded', String(isMenuOpen));
  });

  const content = document.createElement('div');
  content.className = 'storeeel-insert-point-selector-menu-content';

  const scrollArea = document.createElement('div');
  scrollArea.className = 'storeeel-insert-point-selector-menu-scroll';
  content.appendChild(
    createSettingsSection(
      selectedPosition,
      settings,
      onChange,
      onSettingChange,
      onSave,
      getInsertPointSelectorDevice() !== 'mobile',
      frame,
      getCurrentSettings
    )
  );
  scrollArea.appendChild(content);
  panel.appendChild(toggleButton);
  panel.appendChild(scrollArea);
  menu.appendChild(panel);
  return menu;
};

const ensureInsertTargetAttribute = (element) => {
  const existingValue = element.getAttribute(INSERT_SELECTOR_ATTRIBUTE);
  if (existingValue) return existingValue;

  const generatedValue = `storeeel-target-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  element.setAttribute(INSERT_SELECTOR_ATTRIBUTE, generatedValue);
  return generatedValue;
};

const getPreferredSelectorSource = (element, selectorMeta = null) => {
  if (element.id) {
    return {
      insertElementKind: 'id',
      insertElementValue: element.id,
      insertElementIndex: 0,
    };
  }

  const stableClassNames = getSafeStableClassNames(element);
  if (stableClassNames.length > 0) {
    const insertElementValue = stableClassNames[0];
    const candidates = Array.from(document.getElementsByClassName(insertElementValue));
    return {
      insertElementKind: 'class',
      insertElementValue,
      insertElementIndex: Math.max(candidates.indexOf(element), 0),
    };
  }

  const xpath = selectorMeta
    ? buildInsertPointSelectionXPath({ selectorMeta })
    : null;
  if (xpath) {
    return {
      insertElementKind: 'xpath',
      insertElementValue: xpath,
      insertElementIndex: 0,
    };
  }

  return {
    insertElementKind: INSERT_SELECTOR_ATTRIBUTE,
    insertElementValue: ensureInsertTargetAttribute(element),
    insertElementIndex: 0,
  };
};

const buildClassTokenXPathPredicate = (className) => {
  return `contains(concat(' ', normalize-space(@class), ' '), ' ${className} ')`;
};

const buildAttributePredicate = (element) => {
  if (element.id) return `*[@id="${element.id}"]`;

  const dataAttribute = Array.from(element.attributes).find((attribute) => {
    return attribute.name.startsWith('data-')
      && !attribute.name.startsWith('data-storeeel-')
      && attribute.name !== INSERT_SELECTOR_ATTRIBUTE
      && attribute.value;
  });
  if (dataAttribute) return `${element.tagName.toLowerCase()}[@${dataAttribute.name}="${dataAttribute.value}"]`;

  const stableClassNames = getSafeStableClassNames(element);
  if (stableClassNames.length > 0) {
    return `${element.tagName.toLowerCase()}[${buildClassTokenXPathPredicate(stableClassNames[0])}]`;
  }

  return element.tagName.toLowerCase();
};

const resolveUniqueAnchor = (element) => {
  let current = element;
  while (current && current !== document.body) {
    const candidateXPath = `//${buildAttributePredicate(current)}`;
    if (getXPathMatchCount(candidateXPath) === 1) {
      return {
        anchorElement: current,
        anchorXPath: candidateXPath,
      };
    }
    current = current.parentElement;
  }

  return {
    anchorElement: document.body,
    anchorXPath: '//body',
  };
};

const buildRelativeXPathBaseStep = (element) => {
  const tagName = element.tagName.toLowerCase();

  const dataAttribute = Array.from(element.attributes).find((attribute) => {
    return attribute.name.startsWith('data-')
      && !attribute.name.startsWith('data-storeeel-')
      && attribute.name !== INSERT_SELECTOR_ATTRIBUTE;
  });
  if (dataAttribute?.value) {
    return `${tagName}[@${dataAttribute.name}="${dataAttribute.value}"]`;
  }

  const stableClassNames = getSafeStableClassNames(element);
  if (stableClassNames.length > 0) {
    return `${tagName}[${buildClassTokenXPathPredicate(stableClassNames[0])}]`;
  }

  const siblings = Array.from(element.parentElement?.children || []).filter((candidate) => {
    return candidate.tagName === element.tagName;
  });
  if (siblings.length > 1) {
    return `${tagName}[${siblings.indexOf(element) + 1}]`;
  }

  return tagName;
};

const buildRelativeXPathStep = (element, parentElement) => {
  const baseStep = buildRelativeXPathBaseStep(element);
  if (!parentElement) return baseStep;

  const snapshot = getXPathSnapshot(`./${baseStep}`, parentElement);
  if (snapshot.snapshotLength <= 1) return baseStep;

  let matchedIndex = -1;
  for (let index = 0; index < snapshot.snapshotLength; index += 1) {
    if (snapshot.snapshotItem(index) === element) {
      matchedIndex = index + 1;
      break;
    }
  }

  if (matchedIndex === -1) return baseStep;
  return `${baseStep}[${matchedIndex}]`;
};

const buildSelectorMeta = (element) => {
  const { anchorElement, anchorXPath } = resolveUniqueAnchor(element);

  if (anchorElement === element) {
    return {
      anchorXPath,
      relativeXPath: '.',
      resolutionStrategy: 'anchor+xpath',
    };
  }

  const steps = [];
  let current = element;
  while (current && current !== anchorElement) {
    steps.unshift(buildRelativeXPathStep(current, current.parentElement));
    current = current.parentElement;
  }

  return {
    anchorXPath,
    relativeXPath: `./${steps.join('/')}`,
    resolutionStrategy: 'anchor+xpath',
  };
};

export const buildSelectionDetail = (element, position = INSERT_SELECTOR_DEFAULT_POSITION) => {
  const selectorMeta = buildSelectorMeta(element);
  const selector = getPreferredSelectorSource(element, selectorMeta);

  return {
    ...selector,
    position,
    selectorMeta,
  };
};

export const isInsertPointSelectorEnabled = (tagParams = {}) => {
  if (tagParams?.insertPointSelector === true) return true;
  return Boolean(getInsertPointSelectorSearchParams().get(INSERT_SELECTOR_QUERY_KEY));
};

export const getInsertPointSelectorToken = (tagParams = {}) => {
  if (typeof tagParams?.insertPointSelectorToken === 'string' && tagParams.insertPointSelectorToken.length > 0) {
    return tagParams.insertPointSelectorToken;
  }
  return getInsertPointSelectorSearchParams().get(INSERT_SELECTOR_QUERY_KEY);
};

export const getInsertPointSelectorDevice = () => {
  return normalizeInsertPointSelectorDevice(getInsertPointSelectorSearchParams().get(INSERT_SELECTOR_DEVICE_QUERY_KEY));
};

export const getInsertPointSelectorFrame = () => {
  return normalizeInsertPointSelectorFrame(getInsertPointSelectorSearchParams().get(INSERT_SELECTOR_FRAME_QUERY_KEY));
};

export const getCurrentInsertPointSelectorDevice = () => {
  if (window.mockMode === 'pc') return 'pc';
  if (window.mockMode === 'mobile') return 'mobile';

  const userAgent = navigator.userAgent || '';
  const isMobileUserAgent = /iPhone|iPod|Android.*Mobile|Windows Phone/i.test(userAgent);
  const isMobileViewport = window.matchMedia?.('(max-width: 767px)')?.matches === true;

  return (isMobileUserAgent || isMobileViewport) ? 'mobile' : 'pc';
};

export const isInsertPointSelectorDeviceMismatch = (
  expectedDevice = getInsertPointSelectorDevice(),
  currentDevice = getCurrentInsertPointSelectorDevice()
) => {
  return Boolean(expectedDevice && currentDevice && expectedDevice !== currentDevice);
};

const publishSelection = (detail) => {
  window.storeeelInsertPointSelection = detail;
  window.dispatchEvent(new CustomEvent(INSERT_SELECTOR_EVENT_NAME, { detail }));
  const xpath = buildInsertPointSelectionXPath(detail);
  if (!xpath) return;
  window.opener?.postMessage({
    type: INSERT_SELECTOR_SELECTION_MESSAGE_TYPE,
    token: getInsertPointSelectorToken(),
    selection: detail,
    selector: {
      insertElementKind: 'xpath',
      insertElementValue: xpath,
      insertElementIndex: 0,
      position: detail?.position || INSERT_SELECTOR_DEFAULT_POSITION,
    },
  }, '*');
};

const publishInsertSelectorStatus = (status) => {
  if (getInsertPointSelectorDevice() !== 'mobile') return;
  window.opener?.postMessage({
    type: INSERT_SELECTOR_STATUS_MESSAGE_TYPE,
    token: getInsertPointSelectorToken(),
    status,
  }, '*');
};

export const resolveElementByXPath = (expression, contextNode = document) => {
  if (!expression) return null;
  const result = document.evaluate(
    expression,
    contextNode,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  ).singleNodeValue;
  return result instanceof Element ? result : null;
};

export const buildInsertPointSelectionXPath = (detail) => {
  const anchorXPath = detail?.selectorMeta?.anchorXPath;
  const relativeXPath = detail?.selectorMeta?.relativeXPath;
  if (!anchorXPath) return null;
  if (!relativeXPath || relativeXPath === '.') return anchorXPath;
  return `${anchorXPath}${relativeXPath.slice(1)}`;
};

export const buildInsertPointRegistrationPayload = (detail, token, campaignSettings = {}) => {
  const xpath = buildInsertPointSelectionXPath(detail);
  if (!token || !xpath) return null;

  const payload = {
    token,
    selector: {
      insertElementKind: 'xpath',
      insertElementValue: xpath,
      insertElementIndex: 0,
      position: detail?.position || INSERT_SELECTOR_DEFAULT_POSITION,
    },
  };
  if (campaignSettings && Object.keys(campaignSettings).length > 0) {
    payload.campaignSettings = campaignSettings;
  }
  return payload;
};

export const resolveInsertPointSelectionElement = (detail) => {
  if (!detail) return null;

  if (detail.insertElementKind === 'id') {
    return document.getElementById(detail.insertElementValue);
  }

  if (detail.insertElementKind === 'class') {
    const elements = document.getElementsByClassName(detail.insertElementValue);
    return elements[detail.insertElementIndex] || null;
  }

  if (detail.insertElementKind === INSERT_SELECTOR_ATTRIBUTE) {
    return document.querySelector(`[${INSERT_SELECTOR_ATTRIBUTE}="${escapeCssValue(detail.insertElementValue)}"]`);
  }

  if (detail.insertElementKind === 'xpath') {
    return resolveElementByXPath(detail.insertElementValue);
  }

  const selectorValue = detail.insertElementValue;
  if (detail.insertElementKind?.startsWith?.('data-')) {
    return document.querySelector(`[${detail.insertElementKind}="${escapeCssValue(selectorValue)}"]`);
  }

  const anchorXPath = detail.selectorMeta?.anchorXPath;
  const relativeXPath = detail.selectorMeta?.relativeXPath;
  if (!anchorXPath || !relativeXPath) return null;

  const anchorResult = resolveElementByXPath(anchorXPath);
  if (!(anchorResult instanceof Element)) return null;
  if (relativeXPath === '.') return anchorResult;

  return resolveElementByXPath(relativeXPath, anchorResult);
};

export const activateInsertPointSelectorDeviceWarning = ({ expectedDevice, onClose }) => {
  ensurePlaceholderStyle();
  const { backdrop, closeButton } = createDeviceWarningPopup(expectedDevice);

  const cleanupWarning = () => {
    closeButton.removeEventListener('click', handleClose);
    backdrop.remove();
  };

  const handleClose = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    cleanupWarning();
    onClose?.({
      expectedDevice,
      currentDevice: getCurrentInsertPointSelectorDevice(),
      isMatched: !isInsertPointSelectorDeviceMismatch(expectedDevice, getCurrentInsertPointSelectorDevice()),
    });
  };

  closeButton.addEventListener('click', handleClose);
  document.body.appendChild(backdrop);

  return cleanupWarning;
};

export const activateInsertPointSelector = ({
  onSelect,
  frame = INSERT_SELECTOR_DEFAULT_FRAME,
  previewHtml = '',
  previewCampaign = null,
  renderPreviewHtml,
  initialSettings = {},
  settingsToken = '',
}) => {
  ensurePlaceholderStyle();
  const placeholder = createPlaceholder(frame, previewHtml);
  placeholder.dataset.storeeelInsertSelectorDevice = getInsertPointSelectorDevice();
  const overlay = createInsertSelectorOverlay();
  let currentSettings = { ...initialSettings };
  let latestGroupGap = currentSettings.groupGap;
  const groupGapReapplyTimerIds = new Set();
  document.body.dataset.storeeelInsertSelectorActive = 'true';
  const buildGroupGapSettings = () => ({
    ...currentSettings,
    groupGap: latestGroupGap,
  });
  const reapplyGroupGap = (schedule = false) => {
    // autoScrollMode の切り替え後は、保持している groupGap だけを再評価して余白を戻す。
    const groupGapSettings = buildGroupGapSettings();
    reapplyPlaceholderGroupGap(placeholder, groupGapSettings, frame);
    reapplyPlaceholderGroupGap(selectedPlaceholder, groupGapSettings, frame);
    syncPlaceholderDragHandlePosition(selectedPlaceholder, selectedDragHandle);

    if (!schedule) return;

    window.requestAnimationFrame?.(() => {
      const latestSettings = buildGroupGapSettings();
      reapplyPlaceholderGroupGap(placeholder, latestSettings, frame);
      reapplyPlaceholderGroupGap(selectedPlaceholder, latestSettings, frame);
      syncPlaceholderDragHandlePosition(selectedPlaceholder, selectedDragHandle);
    });

    [0, 50].forEach((delay) => {
      const timerId = window.setTimeout(() => {
        groupGapReapplyTimerIds.delete(timerId);
        const latestSettings = buildGroupGapSettings();
        reapplyPlaceholderGroupGap(placeholder, latestSettings, frame);
        reapplyPlaceholderGroupGap(selectedPlaceholder, latestSettings, frame);
        syncPlaceholderDragHandlePosition(selectedPlaceholder, selectedDragHandle);
      }, delay);
      groupGapReapplyTimerIds.add(timerId);
    });
  };
  const updatePlaceholderPreview = (targetPlaceholder) => {
    if (!targetPlaceholder) return;
    if (previewCampaign && typeof renderPreviewHtml === 'function') {
      const normalizedFrame = normalizeInsertPointSelectorFrame(frame);
      targetPlaceholder.innerHTML = wrapPreviewHtmlForFrame(normalizedFrame, renderPreviewHtml(currentSettings));
    }
    ensureInsertSelectorInlineHighlight(targetPlaceholder);
    applyPlaceholderLayoutSettings(targetPlaceholder, currentSettings, frame);
    if (targetPlaceholder === selectedPlaceholder) {
      syncPlaceholderDragHandlePosition(targetPlaceholder, selectedDragHandle);
      window.requestAnimationFrame?.(() => syncPlaceholderDragHandlePosition(targetPlaceholder, selectedDragHandle));
    }
  };
  const updateAllPreviews = () => {
    updatePlaceholderPreview(placeholder);
    updatePlaceholderPreview(selectedPlaceholder);
  };
  const applySyncedSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return;
    const normalizedSettings = normalizeSyncedCampaignSettings(settings);
    const hasAutoScrollModeUpdate = Object.prototype.hasOwnProperty.call(normalizedSettings, 'autoScrollMode')
      || Object.prototype.hasOwnProperty.call(normalizedSettings, 'auto_scroll_mode');
    const hasGroupGapUpdate = Object.prototype.hasOwnProperty.call(normalizedSettings, 'groupGap')
      || Object.prototype.hasOwnProperty.call(normalizedSettings, 'group_gap');
    if (hasGroupGapUpdate) {
      // 自動横スクロールの切り替えと同時に送られた groupGap も落とさず反映する。
      latestGroupGap = normalizedSettings.groupGap;
    }
    const nextGroupGap = hasGroupGapUpdate
      ? normalizedSettings.groupGap
      : latestGroupGap;
    currentSettings = {
      ...currentSettings,
      ...normalizedSettings,
      groupGap: nextGroupGap,
    };
    updateAllPreviews();
    if (hasAutoScrollModeUpdate) {
      reapplyGroupGap(true);
    }
  };
  const handleSettingChange = (key, value) => {
    if (key === 'groupGap') {
      latestGroupGap = value;
    }
    currentSettings = {
      ...currentSettings,
      [key]: value,
    };
    updateAllPreviews();
    if (key === 'autoScrollMode') {
      reapplyGroupGap(true);
    }
  };
  const handleMenuSave = async () => {
    const detail = pendingDetail || (currentTarget ? buildSelectionDetail(currentTarget, currentPosition) : null);
    if (!detail) {
      throw new Error('差し込み位置を選択してください。');
    }
    publishSelection(detail);
    const result = await onSelect?.(detail, currentSettings);
    return result;
  };
  const positionMenu = createPositionMenu(
    INSERT_SELECTOR_DEFAULT_POSITION,
    (nextPosition) => {
      currentPosition = nextPosition;
      if (currentTarget && !isConfirming) {
        movePlaceholder(placeholder, currentTarget, currentPosition);
      }
      if (currentTarget && isConfirming) {
        movePendingSelection(currentTarget, currentPosition);
      }
    },
    currentSettings,
    handleSettingChange,
    handleMenuSave,
    frame,
    () => currentSettings
  );
  const insertSelectorMenuUi = createInsertSelectorUiRoot(2147483647, positionMenu);
  let currentTarget = null;
  let currentPosition = INSERT_SELECTOR_DEFAULT_POSITION;
  let selectedPlaceholder = null;
  let selectedDragHandle = null;
  let confirmPopup = null;
  let pendingDetail = null;
  let isConfirming = false;
  let isHoverSelectionLocked = false;
  let hoverCursorTarget = null;
  let hoverCursorTargetPreviousValue = '';
  let dragPointerId = null;
  let dragCaptureElement = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartPosition = INSERT_SELECTOR_DEFAULT_POSITION;
  let dragLastClientX = 0;
  let dragLastClientY = 0;
  let dragAutoScrollTimerId = null;
  let lastDragAutoScrollDeltaY = 0;
  let lastStableDragSelection = null;
  let previousHtmlOverflowAnchor = '';
  let previousBodyOverflowAnchor = '';
  let hasDragged = false;
  let lastDragUpdateAt = 0;
  let suppressNextClick = false;
  let hoverPlaceholderMoveFrameId = null;
  const getOverlayTargetPlaceholder = () => {
    if (selectedPlaceholder) return selectedPlaceholder;
    if (currentTarget && !isConfirming) return placeholder;
    if (placeholder.dataset.storeeelInsertSelectorOverlayTarget === 'true') return placeholder;
    return null;
  };
  const syncInsertSelectorOverlay = () => {
    const parts = {
      top: overlay.querySelector('[data-storeeel-insert-selector-overlay-part="top"]'),
      right: overlay.querySelector('[data-storeeel-insert-selector-overlay-part="right"]'),
      bottom: overlay.querySelector('[data-storeeel-insert-selector-overlay-part="bottom"]'),
      left: overlay.querySelector('[data-storeeel-insert-selector-overlay-part="left"]'),
      topLeftCorner: overlay.querySelector('[data-storeeel-insert-selector-overlay-corner="top-left"]'),
      topRightCorner: overlay.querySelector('[data-storeeel-insert-selector-overlay-corner="top-right"]'),
      bottomRightCorner: overlay.querySelector('[data-storeeel-insert-selector-overlay-corner="bottom-right"]'),
      bottomLeftCorner: overlay.querySelector('[data-storeeel-insert-selector-overlay-corner="bottom-left"]'),
    };
    const hideParts = () => {
      Object.values(parts).forEach((part) => {
        if (part) part.style.display = 'none';
      });
      [placeholder, selectedPlaceholder].filter(Boolean).forEach((targetPlaceholder) => {
        const highlightHost = getInsertSelectorHighlightHost(targetPlaceholder);
        if (highlightHost) highlightHost.style.setProperty('display', 'none', 'important');
        delete targetPlaceholder.dataset.storeeelInsertSelectorOverlayTarget;
      });
    };
    const clearVisibleBackground = (targetPlaceholder) => {
      if (!targetPlaceholder) return;
      const highlightHost = getInsertSelectorHighlightHost(targetPlaceholder);
      if (highlightHost) highlightHost.style.setProperty('display', 'none', 'important');
      delete targetPlaceholder.dataset.storeeelInsertSelectorOverlayTarget;
    };
    const overlayTargetPlaceholder = getOverlayTargetPlaceholder();
    if (!overlayTargetPlaceholder || document.body.dataset.storeeelInsertSelectorEditMode !== 'position') {
      hideParts();
      return;
    }
    const visibleOccupiedRect = getVisibleOccupiedRect(
      overlayTargetPlaceholder,
      getInsertSelectorOccupiedRect(overlayTargetPlaceholder)
    );
    [placeholder, selectedPlaceholder]
      .filter((targetPlaceholder) => targetPlaceholder && targetPlaceholder !== overlayTargetPlaceholder)
      .forEach(clearVisibleBackground);
    const setInlineHighlightRect = (part, rect) => {
      if (!part) return;
      const placeholderRect = overlayTargetPlaceholder.getBoundingClientRect();
      const left = Math.round(rect.left - placeholderRect.left + overlayTargetPlaceholder.scrollLeft);
      const top = Math.round(rect.top - placeholderRect.top + overlayTargetPlaceholder.scrollTop);
      const right = Math.round(rect.left - placeholderRect.left + overlayTargetPlaceholder.scrollLeft + rect.width);
      const bottom = Math.round(rect.top - placeholderRect.top + overlayTargetPlaceholder.scrollTop + rect.height);
      const width = Math.max(0, right - left);
      const height = Math.max(0, bottom - top);
      part.style.setProperty('display', width > 0 && height > 0 ? 'block' : 'none', 'important');
      part.style.setProperty('left', `${left}px`, 'important');
      part.style.setProperty('top', `${top}px`, 'important');
      part.style.setProperty('width', `${width}px`, 'important');
      part.style.setProperty('height', `${height}px`, 'important');
    };
    const targetHighlightHost = getInsertSelectorHighlightHost(overlayTargetPlaceholder);
    const targetHighlight = getInsertSelectorHighlight(overlayTargetPlaceholder);
    const hideHighlight = () => {
      if (targetHighlightHost) targetHighlightHost.style.setProperty('display', 'none', 'important');
    };

    const setHighlightRect = (rect) => {
      if (!targetHighlightHost || !targetHighlight) return;
      setInlineHighlightRect(targetHighlightHost, rect);
      const width = Math.max(0, Math.round(rect.width));
      const height = Math.max(0, Math.round(rect.height));
      targetHighlight.style.display = width > 0 && height > 0 ? 'block' : 'none';
    };

    if (!visibleOccupiedRect) {
      clearVisibleBackground(overlayTargetPlaceholder);
      [
        parts.topLeftCorner,
        parts.topRightCorner,
        parts.bottomRightCorner,
        parts.bottomLeftCorner,
      ].forEach((part) => {
        if (part) part.style.display = 'none';
      });
      hideHighlight();
      return;
    }

    const { left, top, right, bottom } = visibleOccupiedRect;
    overlayTargetPlaceholder.dataset.storeeelInsertSelectorOverlayTarget = 'true';
    stopHighlightedPlaceholderAutoScroll(overlayTargetPlaceholder);
    setHighlightRect({ left, top, width: right - left, height: bottom - top });
    [
      parts.top,
      parts.right,
      parts.bottom,
      parts.left,
      parts.topLeftCorner,
      parts.topRightCorner,
      parts.bottomRightCorner,
      parts.bottomLeftCorner,
    ].forEach((part) => {
      if (part) part.style.display = 'none';
    });
  };
  const setInsertSelectorEditMode = (mode) => {
    const nextMode = mode === 'custom' ? 'custom' : 'position';
    const statusText = nextMode === 'custom' ? 'カスタムモード' : '配置モード';
    document.body.dataset.storeeelInsertSelectorEditMode = nextMode;
    insertSelectorMenuUi.host.dataset.storeeelInsertSelectorEditMode = nextMode;
    positionMenu.querySelector('[data-storeeel-insert-selector-status-value="true"]')?.replaceChildren(
      document.createTextNode(statusText)
    );
    publishInsertSelectorStatus(statusText);
    syncInsertSelectorOverlay();
  };
  const clearInsertSelectorEditMode = () => {
    delete document.body.dataset.storeeelInsertSelectorEditMode;
    delete insertSelectorMenuUi.host.dataset.storeeelInsertSelectorEditMode;
    syncInsertSelectorOverlay();
  };
  const syncSelectedPlaceholderDragHandlePosition = () => {
    syncPlaceholderDragHandlePosition(selectedPlaceholder, selectedDragHandle);
    syncInsertSelectorOverlay();
  };
  const cancelScheduledHoverPlaceholderMove = () => {
    if (hoverPlaceholderMoveFrameId === null) return;
    window.cancelAnimationFrame?.(hoverPlaceholderMoveFrameId);
    hoverPlaceholderMoveFrameId = null;
  };
  const scheduleHoverPlaceholderMove = (target, position) => {
    cancelScheduledHoverPlaceholderMove();
    hoverPlaceholderMoveFrameId = window.requestAnimationFrame?.(() => {
      hoverPlaceholderMoveFrameId = null;
      if (isConfirming || isHoverSelectionLocked) return;
      if (currentTarget !== target || currentPosition !== position) return;
      movePlaceholder(placeholder, target, position);
      updatePlaceholderPreview(placeholder);
      setInsertSelectorEditMode('position');
    }) ?? window.setTimeout(() => {
      hoverPlaceholderMoveFrameId = null;
      if (isConfirming || isHoverSelectionLocked) return;
      if (currentTarget !== target || currentPosition !== position) return;
      movePlaceholder(placeholder, target, position);
      updatePlaceholderPreview(placeholder);
      setInsertSelectorEditMode('position');
    }, 0);
  };
  const shouldLockHighlightedPlaceholderScroll = () => {
    const device = getCurrentInsertPointSelectorDevice();
    return device === 'pc' || device === 'mobile';
  };
  const resetHighlightedPlaceholderScroll = (targetPlaceholder) => {
    if (!shouldLockHighlightedPlaceholderScroll()) return;
    if (
      !hasVisibleInsertSelectorHighlight(targetPlaceholder)
      && targetPlaceholder?.dataset?.storeeelInsertSelectorOverlayTarget !== 'true'
    ) return;
    stopHighlightedPlaceholderAutoScroll(targetPlaceholder);
    if (targetPlaceholder.scrollLeft !== 0) {
      targetPlaceholder.scrollLeft = 0;
    }
  };
  const stopHighlightedPlaceholderAutoScroll = (targetPlaceholder) => {
    if (typeof targetPlaceholder?.storeeelPauseAutoScroll === 'function') {
      targetPlaceholder.storeeelPauseAutoScroll();
      return;
    }
    if (typeof targetPlaceholder?.storeeelInsertSelectorAutoScrollCleanup !== 'function') return;
    // ハイライト中は横スクロール固定と自動横スクロールが競合するため、位置選択を優先して停止する。
    targetPlaceholder.storeeelInsertSelectorAutoScrollCleanup();
    targetPlaceholder.storeeelInsertSelectorAutoScrollCleanup = null;
  };
  const handlePlaceholderScroll = (event) => {
    resetHighlightedPlaceholderScroll(event.currentTarget);
    syncInsertSelectorOverlay();
  };
  const handleSelectedPlaceholderScroll = (event) => {
    resetHighlightedPlaceholderScroll(event.currentTarget);
    syncSelectedPlaceholderDragHandlePosition();
  };
  const preventPlaceholderWheelOnHighlight = (event) => {
    if (!shouldLockHighlightedPlaceholderScroll()) return;
    const isOverHighlight = isPointOverVisibleInsertSelectorHighlight(event.currentTarget, event.clientX, event.clientY);
    if (!isOverHighlight && !hasVisibleInsertSelectorHighlight(event.currentTarget)) return;

    // ハイライト上の縦スクロールはページへ渡し、横スクロールだけプレビュー内で動かないようにする。
    if (isHorizontalWheelEvent(event)) {
      event.preventDefault();
    }
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };
  const preventDocumentWheelOnHighlightedPlaceholder = (event) => {
    if (!shouldLockHighlightedPlaceholderScroll()) return;
    const targetPlaceholder = [placeholder, selectedPlaceholder].find((target) => (
      target
      && hasVisibleInsertSelectorHighlight(target)
      && (
        isPointOverVisibleInsertSelectorHighlight(target, event.clientX, event.clientY)
        || isPointOverElement(target, event.clientX, event.clientY)
      )
    ));
    if (!targetPlaceholder) return;

    // プレビューの wheel 制御へ伝播させず、縦方向はページ全体のスクロールに任せる。
    if (isHorizontalWheelEvent(event)) {
      event.preventDefault();
    }
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  const handleSettingsMessage = (event) => {
    const data = event?.data;
    if (!data || data.type !== INSERT_SELECTOR_SETTINGS_MESSAGE_TYPE) return;
    if (!settingsToken || data.token !== settingsToken) return;
    applySyncedSettings(data.settings);
  };
  const clearMobileHighlightOnInactive = () => {
    if (getCurrentInsertPointSelectorDevice() !== 'mobile') return;
    clearInsertSelectorEditMode();
  };
  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'hidden') return;
    clearMobileHighlightOnInactive();
  };

  updateAllPreviews();
  document.body.appendChild(overlay);
  document.body.appendChild(insertSelectorMenuUi.host);
  publishInsertSelectorStatus('配置モード');
  placeholder.addEventListener('scroll', handlePlaceholderScroll, { passive: true });
  placeholder.addEventListener('wheel', preventPlaceholderWheelOnHighlight, { passive: false });
  document.addEventListener('wheel', preventDocumentWheelOnHighlightedPlaceholder, { capture: true, passive: false });
  positionMenu.addEventListener('pointerdown', () => setInsertSelectorEditMode('custom'), true);
  positionMenu.addEventListener('click', () => setInsertSelectorEditMode('custom'), true);
  positionMenu.addEventListener('focusin', () => setInsertSelectorEditMode('custom'), true);
  window.addEventListener('message', handleSettingsMessage);
  window.addEventListener('blur', clearMobileHighlightOnInactive);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  const cleanupSelector = () => {
    document.removeEventListener('mousemove', handleMouseMove, true);
    document.removeEventListener('pointerdown', handlePointerDown, true);
    document.removeEventListener('pointermove', handlePointerMove, true);
    document.removeEventListener('pointerup', finishDragging, true);
    document.removeEventListener('pointercancel', finishDragging, true);
    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('dblclick', handleDoubleClick, true);
    document.removeEventListener('touchend', handleTouchEnd, true);
    window.removeEventListener('resize', syncConfirmPopupPosition, true);
    window.removeEventListener('scroll', syncConfirmPopupPosition, true);
    document.removeEventListener('scroll', syncConfirmPopupPosition, true);
    window.removeEventListener('message', handleSettingsMessage);
    window.removeEventListener('blur', clearMobileHighlightOnInactive);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    groupGapReapplyTimerIds.forEach((timerId) => window.clearTimeout(timerId));
    groupGapReapplyTimerIds.clear();
    cancelScheduledHoverPlaceholderMove();
    stopDragAutoScroll();
    restoreDragScrollAnchoring();
    clearHoverCursorTarget();
    stopPlaceholderPreviewEffects(placeholder);
    stopPlaceholderPreviewEffects(selectedPlaceholder);
    placeholder.removeEventListener('scroll', handlePlaceholderScroll);
    placeholder.removeEventListener('wheel', preventPlaceholderWheelOnHighlight);
    selectedPlaceholder?.removeEventListener('scroll', handleSelectedPlaceholderScroll);
    selectedPlaceholder?.removeEventListener('wheel', preventPlaceholderWheelOnHighlight);
    document.removeEventListener('wheel', preventDocumentWheelOnHighlightedPlaceholder, true);
    placeholder.remove();
    selectedDragHandle?.remove();
    confirmPopup?.remove();
    overlay.remove();
    insertSelectorMenuUi.host.remove();
    delete document.body.dataset.storeeelInsertSelectorActive;
    delete document.body.dataset.storeeelInsertSelectorEditMode;
  };

  const shouldUseConfirmPopup = () => false;

  const placeConfirmPopup = () => {
    if (!selectedPlaceholder || !confirmPopup) return;
    if (!document.body.contains(confirmPopup)) {
      document.body.appendChild(confirmPopup);
    }

    const placeholderRect = selectedPlaceholder.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const maxPopupWidth = Math.max(0, viewportWidth - (INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN * 2));

    confirmPopup.style.visibility = 'hidden';
    confirmPopup.style.left = '0px';
    confirmPopup.style.top = '0px';
    confirmPopup.style.maxWidth = `${maxPopupWidth}px`;

    const popupRect = confirmPopup.getBoundingClientRect();
    const maxLeft = Math.max(
      INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN,
      viewportWidth - popupRect.width - INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN
    );
    const maxTop = Math.max(
      INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN,
      viewportHeight - popupRect.height - INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN
    );

    // スマホでは差し込み位置を上半分に固定したまま、確認モーダルをその直下へ置く。
    const left = Math.max(
      INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN,
      Math.min(placeholderRect.left, maxLeft)
    );
    const top = Math.max(
      INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN,
      Math.min(placeholderRect.bottom + INSERT_SELECTOR_CONFIRM_POPUP_VIEWPORT_MARGIN, maxTop)
    );

    confirmPopup.style.left = `${left}px`;
    confirmPopup.style.top = `${top}px`;
    confirmPopup.style.visibility = 'visible';
  };

  const syncConfirmPopupPosition = () => {
    if (!isConfirming) {
      return;
    }
    syncPlaceholderDragHandlePosition(selectedPlaceholder, selectedDragHandle);
    syncInsertSelectorOverlay();
    placeConfirmPopup();
  };

  const getCheckedInsertPosition = () => {
    const checkedInput = positionMenu.querySelector('input[name="storeeel-insert-position"]:checked');
    return checkedInput?.value || currentPosition;
  };

  const syncPositionMenuSelection = (position) => {
    positionMenu.querySelectorAll('input[name="storeeel-insert-position"]').forEach((input) => {
      input.checked = input.value === position;
    });
  };

  const movePendingSelection = (target, position) => {
    if (!selectedPlaceholder || !target?.parentNode) return;
    currentTarget = target;
    currentPosition = position;
    pendingDetail = buildSelectionDetail(target, position);
    publishSelection(pendingDetail);
    movePlaceholder(selectedPlaceholder, target, position);
    // 移動先の幅に応じて再計算し、狭い領域での左寄せ状態を引きずらないようにする。
    updatePlaceholderPreview(selectedPlaceholder);
    syncInsertSelectorOverlay();
    syncPositionMenuSelection(position);
    placeConfirmPopup();
  };

  const findDragTargetFromPoint = (clientX, clientY) => {
    if (!selectedPlaceholder) return null;

    const previousPlaceholderVisibility = selectedPlaceholder.style.visibility;
    const previousPlaceholderPointerEvents = selectedPlaceholder.style.pointerEvents;
    const previousPlaceholderDisplay = selectedPlaceholder.style.display;

    // ドラッグ中のプレビュー自身がレイアウトを押し下げた座標ではなく、画面上のドロップ位置で対象要素を判定する。
    selectedPlaceholder.style.display = 'none';
    selectedPlaceholder.style.visibility = 'hidden';
    selectedPlaceholder.style.pointerEvents = 'none';
    const previousDragHandleDisplay = selectedDragHandle?.style.display;
    if (selectedDragHandle) selectedDragHandle.style.display = 'none';

    const hoveredElement = document.elementsFromPoint?.(clientX, clientY)
      ?.find((element) => !isExcludedElement(element))
      || document.elementFromPoint(clientX, clientY);
    const baseTarget = getPointDragTarget(hoveredElement);
    const nextTarget = getNearestDragTarget(baseTarget, clientX, clientY) || baseTarget;
    const fallbackPosition = dragPointerId !== null ? dragStartPosition : currentPosition;
    const nextPosition = stabilizeDragInsertPosition(
      nextTarget,
      clientY,
      resolveDragInsertPosition(nextTarget, clientY, fallbackPosition),
      fallbackPosition
    );

    selectedPlaceholder.style.display = previousPlaceholderDisplay;
    selectedPlaceholder.style.visibility = previousPlaceholderVisibility;
    selectedPlaceholder.style.pointerEvents = previousPlaceholderPointerEvents;
    if (selectedDragHandle) selectedDragHandle.style.display = previousDragHandleDisplay;
    syncInsertSelectorOverlay();

    return { target: nextTarget, position: nextPosition };
  };

  const clearPendingSelection = () => {
    cancelScheduledHoverPlaceholderMove();
    stopDragAutoScroll();
    restoreDragScrollAnchoring();
    lastDragAutoScrollDeltaY = 0;
    stopPlaceholderPreviewEffects(selectedPlaceholder);
    selectedPlaceholder?.removeEventListener('scroll', syncSelectedPlaceholderDragHandlePosition);
    selectedPlaceholder?.remove();
    selectedDragHandle?.remove();
    confirmPopup?.remove();
    selectedPlaceholder = null;
    selectedDragHandle = null;
    confirmPopup = null;
    pendingDetail = null;
    isConfirming = false;
    isHoverSelectionLocked = false;
    dragPointerId = null;
    dragCaptureElement = null;
    dragStartPosition = INSERT_SELECTOR_DEFAULT_POSITION;
    lastStableDragSelection = null;
    hasDragged = false;
    lastDragUpdateAt = 0;
    syncInsertSelectorOverlay();
  };

  const getDragAutoScrollDelta = (clientX, clientY) => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (viewportHeight <= 0) return 0;
    const menu = getCurrentInsertPointSelectorDevice() === 'mobile'
      ? positionMenu
      : null;
    const menuRect = menu?.getBoundingClientRect?.();
    const isPointInMenuX = menuRect && clientX >= menuRect.left && clientX <= menuRect.right;
    const bottomBoundary = isPointInMenuX && menuRect.top > viewportHeight / 2 && menuRect.top < viewportHeight
      ? menuRect.top
      : viewportHeight;

    if (clientY < INSERT_SELECTOR_DRAG_AUTO_SCROLL_EDGE_PX) {
      return -Math.ceil(
        ((INSERT_SELECTOR_DRAG_AUTO_SCROLL_EDGE_PX - clientY) / INSERT_SELECTOR_DRAG_AUTO_SCROLL_EDGE_PX)
        * INSERT_SELECTOR_DRAG_AUTO_SCROLL_MAX_STEP_PX
      );
    }

    const bottomEdgeTop = bottomBoundary - INSERT_SELECTOR_DRAG_AUTO_SCROLL_EDGE_PX;
    if (clientY > bottomEdgeTop && clientY < bottomBoundary) {
      return Math.ceil(
        ((clientY - bottomEdgeTop) / INSERT_SELECTOR_DRAG_AUTO_SCROLL_EDGE_PX)
        * INSERT_SELECTOR_DRAG_AUTO_SCROLL_MAX_STEP_PX
      );
    }

    return 0;
  };

  const stopDragAutoScroll = () => {
    if (!dragAutoScrollTimerId) return;
    window.clearInterval(dragAutoScrollTimerId);
    dragAutoScrollTimerId = null;
  };

  const startDragAutoScroll = () => {
    if (dragAutoScrollTimerId) return;

    dragAutoScrollTimerId = window.setInterval(() => {
      if (!isConfirming || dragPointerId === null) {
        stopDragAutoScroll();
        return;
      }

      applyDragAutoScroll();
    }, INSERT_SELECTOR_DRAG_AUTO_SCROLL_INTERVAL_MS);
  };

  const disableDragScrollAnchoring = () => {
    previousHtmlOverflowAnchor = document.documentElement.style.overflowAnchor;
    previousBodyOverflowAnchor = document.body.style.overflowAnchor;
    document.documentElement.style.overflowAnchor = 'none';
    document.body.style.overflowAnchor = 'none';
  };

  const restoreDragScrollAnchoring = () => {
    document.documentElement.style.overflowAnchor = previousHtmlOverflowAnchor;
    document.body.style.overflowAnchor = previousBodyOverflowAnchor;
    previousHtmlOverflowAnchor = '';
    previousBodyOverflowAnchor = '';
  };

  const clearHoverCursorTarget = () => {
    if (!hoverCursorTarget) return;
    hoverCursorTarget.style.cursor = hoverCursorTargetPreviousValue;
    hoverCursorTarget = null;
    hoverCursorTargetPreviousValue = '';
  };

  const setHoverCursorTarget = (target) => {
    if (hoverCursorTarget === target) return;
    clearHoverCursorTarget();
    if (!target) return;

    hoverCursorTarget = target;
    hoverCursorTargetPreviousValue = target.style.cursor;
    target.style.cursor = 'grab';
  };

  const findHoverSelectionFromPoint = (event) => {
    if (getCurrentInsertPointSelectorDevice() !== 'pc') {
      return {
        target: getHoverTarget(event.target),
        position: currentPosition,
      };
    }

    if (currentTarget?.parentNode && isPointOverElement(placeholder, event.clientX, event.clientY)) {
      // hover プレビュー上の空白では背面の body などが拾われるため、現在の選択を維持してハイライトを消さない。
      return {
        target: currentTarget,
        position: currentPosition,
      };
    }

    const shouldKeepCurrentTarget = event.target instanceof Element && isStoreeelElement(event.target);
    const previousPlaceholderVisibility = placeholder.style.visibility;
    const previousPlaceholderPointerEvents = placeholder.style.pointerEvents;
    const previousPlaceholderDisplay = placeholder.style.display;

    // PC hover はプレビュー自身を一時的に外し、D&D と同じく画面座標から背面の差し込み対象を判定する。
    placeholder.style.display = 'none';
    placeholder.style.visibility = 'hidden';
    placeholder.style.pointerEvents = 'none';

    const hoveredElement = document.elementsFromPoint?.(event.clientX, event.clientY)
      ?.find((element) => !isExcludedElement(element))
      || document.elementFromPoint(event.clientX, event.clientY)
      || event.target;
    const baseTarget = getPointDragTarget(hoveredElement);
    const eventTarget = getHoverTarget(event.target);
    const nearestTarget = getNearestDragTarget(baseTarget, event.clientX, event.clientY)
      || baseTarget
      || getHoverTarget(hoveredElement)
      || eventTarget;
    const nextTarget = preferNearbyStableClassTarget(nearestTarget, eventTarget);
    const nextPosition = stabilizeDragInsertPosition(
      nextTarget,
      event.clientY,
      resolveDragInsertPosition(nextTarget, event.clientY, currentPosition),
      currentPosition
    );

    placeholder.style.display = previousPlaceholderDisplay;
    placeholder.style.visibility = previousPlaceholderVisibility;
    placeholder.style.pointerEvents = previousPlaceholderPointerEvents;

    if (!nextTarget && shouldKeepCurrentTarget && currentTarget?.parentNode) {
      return {
        target: currentTarget,
        position: currentPosition,
      };
    }

    return {
      target: nextTarget,
      position: nextPosition,
    };
  };

  const updateTarget = (event) => {
    if (isConfirming || isHoverSelectionLocked) return;
    const nextSelection = findHoverSelectionFromPoint(event);
    const nextTarget = nextSelection?.target;
    const nextPosition = nextSelection?.position || currentPosition;
    if (!nextTarget || !nextTarget.parentNode) {
      clearHoverCursorTarget();
      currentTarget = null;
      if (!selectedPlaceholder) clearInsertSelectorEditMode();
      return;
    }
    currentTarget = nextTarget;
    currentPosition = nextPosition;
    setHoverCursorTarget(nextTarget);
    syncPositionMenuSelection(nextPosition);
    scheduleHoverPlaceholderMove(nextTarget, nextPosition);
  };

  const handleMouseMove = (event) => {
    updateTarget(event);
  };

  const handlePointerDown = (event) => {
    if (!isConfirming || !selectedPlaceholder) return;
    if (!(event.target instanceof Element)) return;
    const isInsideSelectedPlaceholder = Boolean(event.target.closest('[data-storeeel-insert-selector-selected="true"]'));
    if (!isInsideSelectedPlaceholder) {
      return;
    }

    setInsertSelectorEditMode('position');
    dragPointerId = event.pointerId;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    dragStartPosition = pendingDetail?.position || getCheckedInsertPosition();
    dragLastClientX = event.clientX;
    dragLastClientY = event.clientY;
    lastDragAutoScrollDeltaY = 0;
    lastStableDragSelection = null;
    hasDragged = false;
    lastDragUpdateAt = 0;
    selectedPlaceholder.dataset.storeeelInsertSelectorDragging = 'true';
    dragCaptureElement = event.target;
    dragCaptureElement.setPointerCapture?.(event.pointerId);
    disableDragScrollAnchoring();
    startDragAutoScroll();
    event.preventDefault();
    event.stopPropagation();
  };

  const updateDraggingSelection = (event, force = false, allowMenuPoint = false, skipThrottle = false) => {
    if (!isConfirming || dragPointerId !== event.pointerId) return false;

    const movedDistance = Math.abs(event.clientX - dragStartX) + Math.abs(event.clientY - dragStartY);
    if (movedDistance > 4) hasDragged = true;

    const updateAt = Number.isFinite(event.timeStamp) ? event.timeStamp : Date.now();
    if (!force && !skipThrottle && lastDragUpdateAt > 0 && updateAt - lastDragUpdateAt < INSERT_SELECTOR_DRAG_UPDATE_INTERVAL_MS) {
      return false;
    }

    if (
      force
      && getCurrentInsertPointSelectorDevice() === 'mobile'
      && lastDragAutoScrollDeltaY === 0
      && lastStableDragSelection?.target?.parentNode
      && Math.hypot(
        event.clientX - lastStableDragSelection.clientX,
        event.clientY - lastStableDragSelection.clientY
      ) <= 16
    ) {
      // DevTools のスマホエミュレーションでは pointerup 時の再判定がレイアウト変化で滑りやすいため、最後に安定した候補を優先する。
      movePendingSelection(lastStableDragSelection.target, lastStableDragSelection.position);
      lastDragUpdateAt = updateAt;
      return true;
    }

    if (!allowMenuPoint
      && getCurrentInsertPointSelectorDevice() === 'mobile'
      && isPointOverInsertSelectorMenu(event.clientX, event.clientY)) {
      return false;
    }

    const nextSelection = findDragTargetFromPoint(event.clientX, event.clientY);
    let nextTarget = nextSelection?.target;
    if (!nextTarget || !nextTarget.parentNode) return false;
    let nextPosition = nextSelection.position;
    if (force && lastDragAutoScrollDeltaY > 0) {
      nextPosition = 'outer_after';
    } else if (force && lastDragAutoScrollDeltaY < 0) {
      nextPosition = 'outer_before';
    } else if (force && getCurrentInsertPointSelectorDevice() === 'pc' && getCheckedInsertPosition() === 'outer_after') {
      nextPosition = 'outer_after';
    }

    movePendingSelection(nextTarget, nextPosition);
    lastStableDragSelection = {
      target: nextTarget,
      position: nextPosition,
      clientX: event.clientX,
      clientY: event.clientY,
    };
    lastDragUpdateAt = updateAt;
    return true;
  };

  const applyDragAutoScroll = () => {
    if (!isConfirming || dragPointerId === null) return false;
    const deltaY = getDragAutoScrollDelta(dragLastClientX, dragLastClientY);
    if (deltaY === 0) {
      lastDragAutoScrollDeltaY = 0;
      return false;
    }

    lastDragAutoScrollDeltaY = deltaY;
    window.scrollBy(0, deltaY);
    // 自動スクロールでポインタ下の要素が変わるため、同じ画面座標で差し込み先を追従させる。
    updateDraggingSelection({
      pointerId: dragPointerId,
      clientX: dragLastClientX,
      clientY: dragLastClientY,
      timeStamp: Date.now(),
    }, true);
    return true;
  };

  const handlePointerMove = (event) => {
    if (!isConfirming || dragPointerId !== event.pointerId) return;

    dragLastClientX = event.clientX;
    dragLastClientY = event.clientY;
    if (!applyDragAutoScroll()) {
      updateDraggingSelection(event, false, false, true);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const finishDragging = (event) => {
    if (dragPointerId !== event.pointerId) return;
    dragLastClientX = event.clientX;
    dragLastClientY = event.clientY;
    if (hasDragged && lastStableDragSelection?.target?.parentNode) {
      // ドロップ時に再判定すると、レイアウト変化で見えていた差し込み位置から滑るため最後の表示位置で確定する。
      movePendingSelection(lastStableDragSelection.target, lastStableDragSelection.position);
    } else {
      updateDraggingSelection(event, true);
    }
    stopDragAutoScroll();
    restoreDragScrollAnchoring();
    if (dragCaptureElement?.hasPointerCapture?.(event.pointerId)) {
      dragCaptureElement.releasePointerCapture?.(event.pointerId);
    }
    if (selectedPlaceholder) {
      delete selectedPlaceholder.dataset.storeeelInsertSelectorDragging;
    }
    suppressNextClick = hasDragged;
    dragPointerId = null;
    dragCaptureElement = null;
    dragStartPosition = INSERT_SELECTOR_DEFAULT_POSITION;
    dragLastClientX = 0;
    dragLastClientY = 0;
    lastDragAutoScrollDeltaY = 0;
    lastStableDragSelection = null;
    hasDragged = false;
    lastDragUpdateAt = 0;
  };

  const handleClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-storeeel-insert-selector-confirm="true"]')) {
      return;
    }
    if (event.target instanceof Element && event.target.closest('[data-storeeel-insert-selector-menu="true"]')) {
      return;
    }
    if (event.target instanceof Element && event.target.closest(`[${INSERT_SELECTOR_UI_ROOT_ATTRIBUTE}="true"]`)) {
      return;
    }
    if (event.target instanceof Element && event.target.closest('.Toastify')) {
      return;
    }
    if (event.target instanceof Element && event.target.closest('[data-storeeel-insert-selector-selected="true"]')) {
      return;
    }
    if (suppressNextClick) {
      suppressNextClick = false;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      return;
    }
    if (isConfirming) {
      // 一度選択した後のタップでは再選択しない。位置変更はドラッグ&ドロップで行う。
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      return;
    }

    // hover で表示中の位置をそのまま確定位置に使い、menu切替後のズレを防ぐ
    const eventTarget = getHoverTarget(event.target);
    const target = preferNearbyStableClassTarget(currentTarget, eventTarget) || eventTarget;
    if (!target) return;
    const displayedPosition = placeholder.nextElementSibling === target
      ? 'outer_before'
      : target.nextElementSibling === placeholder
        ? 'outer_after'
        : currentPosition;
    currentPosition = displayedPosition;
    syncPositionMenuSelection(currentPosition);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    clearPendingSelection();
    pendingDetail = buildSelectionDetail(target, currentPosition);
    publishSelection(pendingDetail);
    // 一度クリックで場所を決めた後は、hover では移動させずドラッグ操作だけを受け付ける。
    isHoverSelectionLocked = true;
    clearHoverCursorTarget();
    placeholder.remove();
    selectedPlaceholder = placeholder.cloneNode(true);
    selectedPlaceholder.removeAttribute('id');
    selectedPlaceholder.classList.add(INSERT_SELECTOR_PLACEHOLDER_CLASS);
    selectedPlaceholder.dataset.storeeelInsertSelectorSelected = 'true';
    selectedPlaceholder.addEventListener('scroll', handleSelectedPlaceholderScroll, { passive: true });
    selectedPlaceholder.addEventListener('wheel', preventPlaceholderWheelOnHighlight, { passive: false });
    updatePlaceholderPreview(selectedPlaceholder);
    insertElementByPosition(selectedPlaceholder, target, currentPosition);
    updatePlaceholderPreview(selectedPlaceholder);
    syncInsertSelectorOverlay();
    selectedPlaceholder.querySelectorAll('img, video').forEach((element) => {
      // プレビュー内メディアのネイティブドラッグを止め、差し込み位置ドラッグを優先する。
      element.setAttribute('draggable', 'false');
      element.style.setProperty('-webkit-user-drag', 'none');
    });
    isConfirming = true;
    setInsertSelectorEditMode('position');

    if (!shouldUseConfirmPopup()) return;

    const { popup, confirmButton, cancelButton } = createConfirmPopup();
    confirmPopup = popup;
    placeConfirmPopup();

    const setConfirmStatus = (kind, message) => {
      if (kind === 'success') {
        showInsertPointSelectorToast(message || '差し込み位置を登録しました。', 'success');
        return;
      }
      if (kind === 'loading') return;
      showInsertPointSelectorToast(message || '差し込み位置の登録に失敗しました。', 'error');
    };

    const setSubmittingState = (isSubmitting) => {
      confirmButton.disabled = isSubmitting;
      cancelButton.disabled = isSubmitting;
      confirmButton.style.opacity = isSubmitting ? '0.6' : '1';
      cancelButton.style.opacity = isSubmitting ? '0.6' : '1';
      confirmButton.style.cursor = isSubmitting ? 'default' : 'pointer';
      cancelButton.style.cursor = isSubmitting ? 'default' : 'pointer';
    };

    confirmButton.addEventListener('click', async (confirmEvent) => {
      confirmEvent.preventDefault();
      confirmEvent.stopPropagation();
      confirmEvent.stopImmediatePropagation?.();
      if (!pendingDetail) return;
      publishSelection(pendingDetail);
      setSubmittingState(true);
      setConfirmStatus('loading', '差し込み位置を保存しています...');
      try {
        const result = await onSelect?.(pendingDetail, currentSettings);
        setConfirmStatus('success', result?.message || '差し込み位置を登録しました。');
      } catch (error) {
        setSubmittingState(false);
        setConfirmStatus('error', error?.message || '差し込み位置の登録に失敗しました。');
      }
    });

    cancelButton.addEventListener('click', (cancelEvent) => {
      cancelEvent.preventDefault();
      cancelEvent.stopPropagation();
      cancelEvent.stopImmediatePropagation?.();
      const previousTarget = currentTarget;
      clearPendingSelection();
      if (previousTarget) movePlaceholder(placeholder, previousTarget, currentPosition);
    });
  };

  // キャンペーン設置後は、D&Dに加えてダブルクリック（モバイルはダブルタップ）でも
  // ダブルクリック/タップした要素付近へ移動できるようにする。
  // PC の dblclick とモバイルの自前ダブルタップ判定で同じ除外判定・移動処理を使い回すため、判定部分を関数として切り出す。
  const isExcludedFromDoubleTapMove = (rawTarget) => {
    if (!(rawTarget instanceof Element)) return true;
    if (rawTarget.closest('[data-storeeel-insert-selector-confirm="true"]')) return true;
    if (rawTarget.closest('[data-storeeel-insert-selector-menu="true"]')) return true;
    if (rawTarget.closest(`[${INSERT_SELECTOR_UI_ROOT_ATTRIBUTE}="true"]`)) return true;
    if (rawTarget.closest('.Toastify')) return true;
    if (rawTarget.closest('[data-storeeel-insert-selector-selected="true"]')) return true;
    return false;
  };
  // D&Dの座標判定はプレースホルダーを一時非表示にするためレイアウトがずれる。
  // ダブルクリック/ダブルタップは実際に操作された要素そのものを起点に、画面上の見た目どおり判定する。
  const resolveDoubleTapMoveTarget = (rawTarget, clientY) => {
    const nextTarget = getHoverTarget(rawTarget);
    if (!nextTarget?.parentNode) return null;
    return { target: nextTarget, position: resolveDragInsertPosition(nextTarget, clientY, currentPosition) };
  };
  const canHandleDoubleTapMoveAt = (rawTarget, clientX, clientY) => {
    if (!isConfirming || !selectedPlaceholder) return false;
    if (isExcludedFromDoubleTapMove(rawTarget)) return false;
    if (isPointOverInsertSelectorMenu(clientX, clientY)) return false;
    return true;
  };

  // モバイル（iOS Safari 等）は dblclick がネイティブ発火しないため、touchend の連続発生を自前で計測してダブルタップを検出する。
  const DOUBLE_TAP_INTERVAL_MS = 350; // OS標準のダブルタップ判定時間に近い値
  const DOUBLE_TAP_DISTANCE_PX = 24; // 指のブレを許容する距離しきい値
  let lastTapAt = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  // 一部のモバイルブラウザは高速な連続タップから dblclick を合成することがあるため、
  // touchend 側で処理済みの場合はその後に届く dblclick を二重処理しないよう抑止する。
  let suppressNextDoubleClick = false;

  const handleDoubleClick = (event) => {
    if (suppressNextDoubleClick) {
      suppressNextDoubleClick = false;
      return;
    }
    if (!canHandleDoubleTapMoveAt(event.target, event.clientX, event.clientY)) return;

    const move = resolveDoubleTapMoveTarget(event.target, event.clientY);
    if (!move) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    movePendingSelection(move.target, move.position);
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    if (!canHandleDoubleTapMoveAt(touch.target, touch.clientX, touch.clientY)) return;

    const tappedAt = Number.isFinite(event.timeStamp) ? event.timeStamp : Date.now();
    const distance = Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY);
    const isDoubleTap = lastTapAt > 0
      && (tappedAt - lastTapAt) <= DOUBLE_TAP_INTERVAL_MS
      && distance <= DOUBLE_TAP_DISTANCE_PX;

    if (!isDoubleTap) {
      lastTapAt = tappedAt;
      lastTapX = touch.clientX;
      lastTapY = touch.clientY;
      return;
    }

    // 2回目のタップを消費したら連続タップ判定をリセットし、3回目以降を誤検出しないようにする。
    lastTapAt = 0;
    lastTapX = 0;
    lastTapY = 0;

    const move = resolveDoubleTapMoveTarget(touch.target, touch.clientY);
    if (!move) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    suppressNextDoubleClick = true;
    movePendingSelection(move.target, move.position);
  };

  document.addEventListener('mousemove', handleMouseMove, true);
  document.addEventListener('pointerdown', handlePointerDown, true);
  document.addEventListener('pointermove', handlePointerMove, true);
  document.addEventListener('pointerup', finishDragging, true);
  document.addEventListener('pointercancel', finishDragging, true);
  document.addEventListener('click', handleClick, true);
  document.addEventListener('dblclick', handleDoubleClick, true);
  document.addEventListener('touchend', handleTouchEnd, true);
  window.addEventListener('resize', syncConfirmPopupPosition, true);
  window.addEventListener('scroll', syncConfirmPopupPosition, true);
  document.addEventListener('scroll', syncConfirmPopupPosition, true);

  return cleanupSelector;
};

export {
  INSERT_SELECTOR_ATTRIBUTE,
  INSERT_SELECTOR_EVENT_NAME,
  INSERT_SELECTOR_PLACEHOLDER_ID,
};
